import { ChartColumn as ChartImg, Ellipsis, Menu, Settings } from 'lucide-react';
import { Chart } from 'bindings/reporting/Chart';
import { Curve } from 'bindings/reporting/Curve';
import { Link } from 'bindings/reporting/Link';
import { Point } from 'bindings/reporting/Point';
import { SecondaryAxis } from 'bindings/reporting/SecondaryAxis';
import { GenericReport } from 'bindings/reports/generic/GenericReport';
import { Granularity } from 'bindings/time/Granularity';
import arrow from 'images/icons/orders/arrow_left.svg';
import DashboardImg from 'images/menu_icon/dashboard_black.svg';
import VisibilityOffIcon from 'images/reports/visibility_off.svg';
import VisibilityOnIcon from 'images/reports/visibility_on.svg';
import VisibilityOnEmptyIcon from 'images/reports/visibility_on_empty.svg';
import _ from 'lodash';
import * as moment from 'moment';
import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import { PieChart } from 'react-minimal-pie-chart';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AAdditionalColumns, ReportColumnType } from '../../../atoms/additionalColumns';
import { ACampaigns, Campaign } from '../../../atoms/campaignAtom';
import { ACompanies } from '../../../atoms/clientCompany';
import { AStatuses, Status } from '../../../atoms/clientStatusesAtom';
import { ATagFilter } from '../../../atoms/filter/tagsFilterAtom';
import { ADatePicker } from '../../../atoms/filter/timeFilterAtom';
import { AUsers, isOwner, useMe } from '../../../atoms/global/users';
import { AProducts, Product } from '../../../atoms/product';
import { getColorFromList as stringToColour } from '../../../components_v2/avatar/Data/ColorList';
import { Image } from '../../../components_v2/avatar/style/Style';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import { OptionBlock } from '../../../components_v2/dropdown/style/Style';
import AdvancedFilters from '../../../components_v2/filter/AdvancedFilters';
import advancedFiltersInterpretor from '../../../components_v2/filter/AdvancedFiltersInterpretor';
import { FilterResult, FilterTree, filterTreeLength } from '../../../components_v2/filter/model/Model';
import { FilterParameter, FilterParameterParent } from '../../../components_v2/filter/pages/FilterList';
import Input from '../../../components_v2/input/Input';
import { tooltipPos } from '../../../components_v2/models';
import { PanelSelector } from '../../../components_v2/Selector/PanelSelector';
import { InfiniteTable, InfiniteTableStyle } from '../../../components_v2/table/InfiniteTable';
import { getLocalStorage, setLocalStorage } from '../../../helpers/localStorage';
import { Column } from '../../../components_v2/table/Table';
import ToolbarFilterButton from '../../../components_v2/toolbarFilter/ToolbarButton';
import { ColorAndT, createRandomColor, isSuperAdmin } from '../../../components_v2/utils';
import storeLang from '../../../helpers/storeLang';
import { Dot } from '../../../styles/global/css/Dot';
import { rangeOfValues, scaleValueOf, valueColor } from '../../map/colorScale';
import { columnDisplayOptionsOf } from '../generic/columnColors';
import { DefaultImage } from '../../../styles/global/css/GlobalImage';
import { DefaultText, DefaultTextBlack } from '../../../styles/global/css/GlobalText';
import {
	PrimaryColor,
	BorderColor,
	DarkGreySidely2,
	FilterPrimaryColor,
	FilterGreen,
	FilterRed,
	GreenSidely,
	GreySidely,
	GreySidely3,
	RedSidely,
	SidelyBlack,
	SurfaceColor
} from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { useFunctionState } from '../../../utils/customHooks';
import { AlertRes } from '../../alert/AlertProvider';
import useAlert from '../../alert/UseAlert';
import { BodyHeaderStatus } from '../../client-companies/style/PopupStyle';
import { ToolbarImageContainer } from '../../globals/defaultToolbar/style/Style';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import { IconsGroupBackground, SwapImageContainer } from '../../map/PageSwitch';
import NoData from '../../noData/NoData';
import { Owner } from '../../orders/model/Model';
import { FlexDiv } from '../../products/style';
import { deleteGoals, Goal, GoalDelete, GoalEvolutionCalculus, GoalHashMap, GoalPath, postGoals } from '../generic/action';
import { goalsProps, LocalFilters, ReportFiltersContext } from '../generic/generic';
import { getNextGranularity, getStartOfGranularity } from '../generic/import';
import { getColorForPercentage, getNumberFormatter, getStringWidth } from '../utils';
import {
	AxisRepresentation,
	Cell,
	ComplexCell,
	EvolutionTuple,
	Header,
	Panel,
	Reference,
	Report,
	Date as ReportingDate,
	Row,
	TooltipTemplate,
	Value
} from './bareReportingDecoder';
import ChartReportingV2 from './ChartInterpretor';
import ReportInterpretorContext, { EvolutionState, ReportInterpretorContextType, ReportInterpretorProvider, ReportInterpretorProviderProps } from './hoverContext';
import { DefaultInput } from '../../../styles/global/css/GlobalInput';
import { BoolCheckbox } from '../../forms/PopupField';

const MAX_SELECTED = 10;
const PANEL_SELECTOR_HEIGHT = 30;

function stringToGranularity(granularity: string): Granularity {
	const lower = granularity.toLocaleLowerCase();
	switch (lower) {
		case 'day':
		case 'week':
		case 'month':
		case 'quarter':
		case 'year':
			return lower;
		case 'fourmonths':
			return 'fourMonths';
		default:
			return 'month';
	}
}

const EvolutionOperatorContainer = styled.div`
	${DefaultText};
	display: flex;
	align-items: center;
	border-bottom: 1px solid ${DarkGreySidely2};
	height: 100%;
	gap: 5px;
	margin: 0 5px;
	&:after {
		// content: '';
		margin-left: -1px;
		display: inline-block;
		height: 0;
		width: 0;
		border-right: 4px solid transparent;
		border-top: 4px solid ${DarkGreySidely2};
		border-left: 4px solid transparent;
	}
`;

// The value-dependent styles (background, border) go through the style prop in
// Percentage: as styled props, every distinct interpolated color would mint a new
// CSS class that styled-components appends to the stylesheet and never frees
const PercentageDiv = styled(FlexDiv)`
	border-radius: 25px;
	color: ${SidelyBlack};
	text-align: center;
	justify-content: center;
`;

const FlatEvolutionDiv = styled.p<{ value: number }>`
	color: ${({ value }) => value === 0 ? GreySidely : value > 0 ? GreenSidely : RedSidely};
	white-space: nowrap;
	margin: 0;
	font-size: 11px;
	&:before {
		${({ value }) => value === 0 ? 'content: \'=\';' : value > 0 ? 'content: \'+\'' : ''}

	}
`;

const EvolutionDiv = styled.p<{ value: number, kind?: 'small', background?: boolean, fontSize?: string }>`
	white-space: nowrap;
	color: ${({ value }) => value === 0 ? GreySidely : value > 0 ? GreenSidely : RedSidely};
	margin: 0;
	${({ kind, fontSize }) => {
		if (fontSize) return `font-size: ${fontSize};`;
		switch (kind) {
			case 'small': return 'font-size: 10px;';
			default: return 'font-size: 11px;';
		}
	}}
	${({ background, value }) => background ? `
		padding: 0.5em 1em;
		background-color: color-mix(in oklab, ${value === 0 ? GreySidely : value > 0 ? GreenSidely : RedSidely} 19%, transparent);
		border-radius: 5px;
	` : ''}
	&:before {
		${({ value }) => value === 0 ? 'content: \'=\';' : `
			content: '';
			background: url('${arrow}');
		`}
	${({ kind }) => {
		switch (kind) {
			case 'small': return `
					width: 10px;
					height: 7px;
					font-size: 10px;
				`;
			default: return `
					width: 13px;
					height: 8px;
					font-size: 13px;
				`;
		}
	}}
		margin-right: -1px;
		display: inline-block;
		background-size: contain;
		background-repeat: no-repeat;
		background-position: center;
	${({ value }) => {
		if (value === 0) return '';
		return `
				rotate: ${value > 0 ? '' : '-'}90deg;
				filter: ${value > 0 ? FilterGreen : FilterRed};
			`;
	}}
	}
`;

// --------------[VALUE]------------- //
function capitalizeFirstLetter(s: string) {
	return s.charAt(0).toUpperCase() + s.slice(1);
}

export function reportingDateToString(value: ReportingDate): string {
	moment.locale();
	const translate = getTranslate(storeLang.getState().localize);
	if ('day' in value.val) {
		return moment(value.val.day).format('L');
	}
	if ('week' in value.val) {
		const eow = moment(value.val.week).endOf('week');
		return `${translate('symbol_week')}${moment(value.val.week).format('w: Do')} - ${eow.format('Do MMM YY')}`;
	}
	if ('month' in value.val) {
		return capitalizeFirstLetter(moment(value.val.month).format('MMMM YYYY'));
	}
	if ('quarter' in value.val) {
		return `${translate('symbol_quarter')}${moment(value.val.quarter).format('Q YYYY')}`;
	}
	if ('fourMonths' in value.val) {
		const momentDate = moment(value.val.fourMonths);
		const endOf4Months = moment(momentDate);
		endOf4Months.set('month', 3 + momentDate.month());

		return `${momentDate.format('MMM')}-${endOf4Months.format('MMM YYYY')}`;
	}
	if ('year' in value.val) {
		return moment(value.val.year).format('YYYY');
	}
	return '';
}

type Justify = 'center' | 'left' | 'flex-end';

/// Where a value sits in its cell: numbers to the right so their digits line
/// up, words to the left so they read.
///
/// An empty cell is neither. It holds a dash standing in for a value, not a
/// value, so it has no type to take an alignment from — and left-aligned under
/// a column of right-aligned figures it reads as a stray hyphen rather than as
/// nothing. Centred, it is the same mark wherever it appears.
function styleValue(value: Value): { justify?: Justify, className?: string } {
	if (typeof value === 'string' || !value || !value.val) {
		return { justify: 'center' };
	}
	if ('percentage' in value.val) {
		return { justify: 'flex-end' };
	}
	if ('text' in value.val || 'key' in value.val || 'formattedKey' in value.val) {
		return {
			justify: 'left',
			className: 'report-text'
		};
	}
	if ('float' in value.val || 'int' in value.val || 'average' in value.val) {
		return { justify: 'flex-end', className: 'report-number' };
	}
	if ('colorDot' in value.val) {
		return { justify: 'left' };
	}
	if ('pieChart' in value.val) {
		return { justify: 'left' };
	}
	if ('date' in value.val) {
		return { justify: 'left' };
	}
	if ('list' in value.val) {
		return { justify: 'left', className: 'tamer' };
	}
	if ('evolution' in value.val) {
		return { justify: 'left' };
	}
	if ('user' in value.val) {
		return {
			justify: 'left',
			className: 'report-text'
		};
	}
	return {};
}

// The bubble takes the colour its column's scale gives the value when there
// is one, else the historical red to green of the percentage itself.
export function Percentage(props: { percentage: number, width?: string, fontSize?: string, minHeight?: string, goal?: boolean, color?: string }) {
	const { percentage: value } = props;
	const tint = (color: string) => `color-mix(in srgb, ${color} 30%, transparent)`;
	const background = props.color ? (props.goal ? props.color : tint(props.color)) : getColorForPercentage(value, !props.goal ? 0.3 : undefined, undefined, true);
	const border = props.color ? props.color : !props.goal ? getColorForPercentage(value, undefined, undefined, true) : background;
	return <PercentageDiv style={{
		backgroundColor: background,
		border: `1px solid ${border}`,
		width: props.width ?? '80px',
		minHeight: props.minHeight ?? '21px',
		fontWeight: props.goal ? 600 : 400,
		fontSize: props.fontSize
	}}>{(value * 100).toFixed(2)}%</PercentageDiv>;
}

function EvolutionValueDisplayer(props: { value: Extract<ReportColumnType, { 'evolution' }>, columnIndex?: number }) {
	const { evolutionState } = React.useContext(ReportInterpretorContext);
	const state = evolutionState[props.columnIndex ?? -1] ?? '%';
	const res = calcEvolution(props.value.evolution);
	if (!res) return <>-</>;
	if (state === '+') return <FlatEvolutionDiv value={res?.flat}>{res?.flat.toFixed(2)}</FlatEvolutionDiv>;
	if (isNaN(res.percentage)) {
		if (res.flat === 0) return <>-</>;
		return <Evolution percentage={Infinity * res.flat} />;
	}
	return <Evolution percentage={res.percentage} />;

}

export function Evolution(props: { percentage: number, kind?: 'small', background?: boolean, fixed?: number, fontSize?: string }) {
	const { percentage, fixed } = props;
	const tamer = isNaN(percentage) ? 0 : percentage;
	return <EvolutionDiv value={tamer} kind={props.kind} background={props.background} fontSize={props.fontSize}>{isFinite(tamer) ? `${(tamer * 100).toFixed(fixed ?? 2)}%` : '%'}</EvolutionDiv>;
}

export function calcEvolution(evolution: EvolutionTuple): { percentage: number, flat: number } | undefined {
	if (evolution.old.tag !== evolution.new.tag) return undefined;
	if (evolution.old.val && 'percentage' in evolution.old.val && evolution.new.val && 'percentage' in evolution.new.val) {
		return { percentage: fractionToDecimal(evolution.new.val.percentage) - fractionToDecimal(evolution.old.val.percentage), flat: fractionToDecimal(evolution.new.val.percentage) - fractionToDecimal(evolution.old.val.percentage) };
	}
	const oldNumber = valueToNumber(evolution.old) ?? 0;
	const newNumber = valueToNumber(evolution.new) ?? 0;
	return { percentage: (newNumber - oldNumber) / oldNumber, flat: newNumber - oldNumber };
}

function formatHMS(secs) {
	return moment.utc(secs * 1000).format('HH:mm:ss');
}

// `color` is the colour the column's scale gives this value, when it has
// one: a percentage's bubble takes it, a number gets a dot in it.
export function ValueInnerToNode(props: { value: ReportColumnType, emptyTile?: boolean, columnIndex?: number, primary?: boolean, goal?: boolean, formatDuration?: boolean, color?: string }): JSX.Element {
	const { value } = props;
	const withDot = (node: React.ReactNode) => props.color ? <FlexDiv gap='6px' align='center'><Dot color={props.color} size='10px' />{node}</FlexDiv> : <>{node}</>;
	const owners = useRecoilValue(AUsers);
	const products = useRecoilValue(AProducts);
	const companies = useRecoilValue(ACompanies);
	const campaigns = useRecoilValue(ACampaigns);
	const statuses = useRecoilValue(AStatuses);
	const ctx = React.useContext(ReportInterpretorContext);
	const formatter = getNumberFormatter();

	if (typeof value === 'string' || !value) {
		return props.emptyTile ? <></> : <>-</>;
	}
	if ('percentage' in value) {
		return <Percentage percentage={fractionToDecimal(value.percentage)} goal={props.goal} color={props.color} />;
	}
	if ('text' in value) {
		return <>{value.text}</>;
	}
	if ('key' in value) {
		return <Translate id={value.key} />;
	}
	if ('formattedKey' in value) {
		const translate = getTranslate(storeLang.getState().localize);
		let res = translate(value.formattedKey.key).toString();
		value.formattedKey.formats.forEach((replacementKey, i) => res = res?.replaceAll(`{{${i + 1}}}`, replacementKey));
		return <>{res}</>;
	}
	if ('float' in value) {
		return withDot(props.formatDuration ? formatHMS(Math.round(value.float)) : formatter.format(Math.round(value.float * 1e2) / 1e2));
	}
	if ('int' in value) {
		return withDot(props.formatDuration ? formatHMS(value.int) : formatter.format(value.int));
	}
	if ('colorDot' in value) {
		return <Dot color={value.colorDot} size='12px' />;
	}
	/*
		Note to future devs:
		Select fields become piecharts and multi-select fields become lists.
		Piechart and list used to behave differently.
		Now they behave the same, but are serialized in opposite order (piechart is count then name, list is name then count).
		So they still have different code paths and are not merged in the same groups.
		Eventually, pieChart should be removed and everything should become a list.
	*/
	if ('pieChart' in value) {
		if (props.primary) {
			return <CompanyStyle>{value.pieChart.map(d => d.name).join(', ')}</CompanyStyle>;
		}
		const total = value.pieChart.reduce((acc, data) => acc + data.count, 0);
		return <PieChart
			className='circle'
			lineWidth={70}
			center={[50, 50]}
			viewBoxSize={[100, 100]}
			data={value.pieChart.map(data => ({ value: data.count / total, color: stringToColour(data.name) }))}
		/>;
	}
	if ('list' in value) {
		if (props.primary) {
			return <CompanyStyle>{value.list.map(d => d.value).join(', ')}</CompanyStyle>;
		}
		const total = value.list.reduce((acc, data) => acc + data.count, 0);
		return <PieChart
			className='circle'
			lineWidth={70}
			center={[50, 50]}
			viewBoxSize={[100, 100]}
			data={value.list.map(data => ({ value: data.count / total, color: stringToColour(data.value) }))}
		/>;
	}
	if ('date' in value) {
		return <>{reportingDateToString(value.date)}</>;
	}
	if ('datetime' in value) {
		return <>{moment.unix(value.datetime).format('L - LT')}</>;
	}
	if ('user' in value) {
		const id = value.user;
		const name = ctx?.mapValues?.users?.[id] ?? owners.find(o => o.id === id)?.name;
		if (name) return <>{name}</>;
		return <></>;
	}
	if ('evolution' in value) {
		return <EvolutionValueDisplayer value={value} columnIndex={props.columnIndex} />;
	}
	if ('product' in value) {
		const uuid = value.product;
		const name = ctx?.mapValues?.products?.[uuid] ?? products.find(p => p.uuid === uuid)?.name;
		if (name) return <CompanyStyle>{name}</CompanyStyle>;
	}
	if ('company' in value) {
		const name = ctx?.mapValues?.companies?.[value.company] ?? companies[value.company];
		return <CompanyStyle>{name}</CompanyStyle>;
	}
	if ('average' in value) {
		return <>{props.formatDuration ? formatHMS(Math.round(value.average.numerator / value.average.denominator)) : (value.average.numerator / value.average.denominator).toFixed(2)}</>;
	}
	if ('event' in value) {
		return <CompanyStyle>{value.event.title}</CompanyStyle>;
	}
	if ('contact' in value) {
		return <CompanyStyle>{value.contact}</CompanyStyle>;
	}
	if ('campaign' in value) {
		const name = ctx?.mapValues?.campaigns?.[value.campaign] ?? campaigns[value.campaign]?.name;
		return <CompanyStyle>{name}</CompanyStyle>;
	}
	if ('status' in value) {
		const status = statuses[value.status];
		return <>
			<FlexDiv gap="5px">
				<BodyHeaderStatus backgroundColor={status?.status_color}>
					{status?.status_name ?? <NoValueDiv><Translate id='reports.no_value' /></NoValueDiv>}
				</BodyHeaderStatus>
			</FlexDiv>
		</>;
	}
	return <></>;

}

// the colour a column's display options give one of its values
type ValueColor = (value: ReportColumnType) => string | undefined;

function ValueToNode(props: { value: Omit<Value, 'tag'>, emptyTile?: boolean, columnIndex?: number, primary?: boolean, tag?: Pick<Value, 'tag'>['tag'], goal?: boolean, formatDuration?: boolean, valueColor?: ValueColor }): JSX.Element {
	const { value } = props;
	if (props.tag === 16) {
		return <NoValueDiv>
			<Translate id='reports.no_value' />
		</NoValueDiv>;
	}
	if (typeof value === 'string' || !value || !value.val) {
		return props.emptyTile || props.primary ? <></> : <>-</>;
	}
	return <ValueInnerToNode value={value.val} emptyTile={props.emptyTile} columnIndex={props.columnIndex} primary={props.primary} goal={props.goal} formatDuration={props.formatDuration} color={props.valueColor?.(value.val)} />;
}

const CompanyStyle = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	text-wrap: nowrap;
	text-align: left;
	width: 100%;
`;

const NoValueDiv = styled.div`
	font-weight: 200;
	font-style: italic;
`;

type ReactTableRow = {
	cells: readonly ComplexCell[],
	subRows: ReactTableRow[],
	primaryCell: readonly Cell[],
	id: number,
	path?: GoalPath,
	history?: readonly Cell[],
	parent?: ReactTableRow,
	// children were cut server-side (lazy sentinel): expanding must fetch them
	lazy?: boolean,
}

function filterNonEmptyGoal(row: ReactTableRow, formattedDate: string, goalPathsHashMap: Map<string, { value: string; index: number; dashboard: boolean; }>): boolean {
	const currentPathHash = goalPathHash(getPathFromRowWithDate(row, formattedDate));
	const goalValue = goalPathsHashMap.get(currentPathHash);
	return goalValue === undefined;
}

function filterRow(row: ReactTableRow, tree: FilterTree | undefined, formattedDate?: string, goalPathsHashMap?: Map<string, { value: string; index: number; dashboard: boolean; }>): ReactTableRow | undefined {
	if (!tree && !formattedDate) return row;
	const newRow = { ...row };
	newRow.subRows = row.subRows.reduce((acc: ReactTableRow[], r) => {
		const res = filterRow(r, tree, formattedDate, goalPathsHashMap);
		if (res) acc.push(res);
		return acc;
	}, []);
	if (newRow.subRows.length > 0) return newRow;
	let filterApproved = true;
	if (tree) {
		filterApproved = advancedFiltersInterpretor(newRow.cells, tree, {
			empty(value, columnId) {
				if (typeof columnId == 'string') {
					const indexedValue = value[Number(columnId)];
					return indexedValue.value.tag === EmptyCell.tag || indexedValue.value.val === undefined;
				}
				return true;
			},
			not_empty(value, columnId) {
				if (typeof columnId == 'string') {
					const indexedValue = value[Number(columnId)];
					return indexedValue.value.tag !== EmptyCell.tag && indexedValue.value.val !== undefined;
				}
				return false;
			},
		});
	}
	if (formattedDate && goalPathsHashMap) {
		filterApproved = filterApproved && filterNonEmptyGoal(newRow, formattedDate, goalPathsHashMap);
	}
	return filterApproved ? newRow : undefined;
}

function toReactTableData(row: Row, reportHeadersNoDate?: Header[], history?: Cell[], parent?: ReactTableRow): ReactTableRow {
	const currentRow: ReactTableRow = {
		id: row.id,
		cells: row.cells,
		primaryCell: row.primaryCell,
		path: reportHeadersNoDate ? (history ?? row.primaryCell ?? []).reduce((acc, current: Cell, i: number) => {
			if (!current)
				return acc;
			const path = current.value.val;
			const objectTitle = Object.values(reportHeadersNoDate[i].cell.value.val ?? {})[0];
			if (path) {
				acc[objectTitle] = {
					'value': path,
					'index': i
				};
			}
			return acc;
		}, {}) : undefined,
		subRows: [],
		history,
		parent
	};
	currentRow.subRows = row.rows.map(subRow => toReactTableData(subRow, reportHeadersNoDate, history ? [...history, subRow.primaryCell[subRow.primaryCell.length - 1]] : [row.primaryCell[row.primaryCell.length - 1], subRow.primaryCell[subRow.primaryCell.length - 1]], currentRow));
	// a single negative-id child bearing the lazy key is the server's
	// truncation sentinel: this node's real children load on expansion. The
	// key sits in the last primary cell — sentinels are padded like a real
	// row of their level so the loading label lands in its own title column.
	const onlyChild = row.rows.length === 1 && row.rows[0].id < 0 ? row.rows[0] : undefined;
	currentRow.lazy = (onlyChild?.primaryCell[onlyChild.primaryCell.length - 1]?.value.val as { key?: string } | null)?.key === 'reports.lazy_loading';
	return currentRow;
}

function valueInnerToString(value: ReportColumnType, users?: Owner[] | undefined, products?: Product[] | undefined, companies?: Record<number, string> | undefined, campaigns?: Record<number, Campaign> | undefined, noDateFormatting?: boolean): string | undefined {
	if (typeof value === 'string' || !value) {
		return '';
	} else if ('text' in value) {
		return value.text;
	} else if ('key' in value) {
		return translateToString(value.key);
	} else if ('formattedKey' in value) {
		let res = translateToString(value.formattedKey.key);
		value.formattedKey.formats.forEach((replacementKey, i) => res = res?.replaceAll(`{{${i + 1}}}`, replacementKey));
		return res;
	} else if ('int' in value) {
		return value.int.toString();
	} else if ('float' in value) {
		return value.float.toFixed(2);
	} else if ('date' in value) {
		if (noDateFormatting) {
			return Object.values(value.date.val)[0];
		}
		return reportingDateToString(value.date);
	} else if ('evolution' in value) {
		return value.evolution.toString();
	} else if ('company' in value) {
		return companies?.[value.company] ?? `Company #${value.company}`;
	} else if ('list' in value) {
		return value.list.map(e => e.value).join(', ');
	} else if ('user' in value) {
		return users?.find(u => u.id === value.user)?.name ?? `User #${value.user}`;
	} else if ('datetime' in value) {
		return moment.unix(value.datetime).format('L - LT');
	} else if ('product' in value) {
		return products?.find(p => p.uuid === value.product)?.name ?? `Product ${value.product}`;
	} else if ('colorDot' in value) {
		return value.colorDot;
	} else if ('average' in value) {
		return (value.average.numerator / value.average.denominator).toFixed(2);
	} else if ('event' in value) {
		return value.event.title;
	} else if ('contact' in value) {
		return `Contact #${value.contact}`;
	} else if ('campaign' in value) {
		return campaigns?.[value.campaign]?.name ?? `Campaign #${value.campaign}`;
	} else {
		console.warn('unimplemented value :', value);
	}
}

export function valueToString(value: Value, users?: Owner[], products?: Product[], companies?: Record<number, string>, campaigns?: Record<number, Campaign>, noDateFormatting?: boolean): string | undefined {
	if (typeof value === 'string' || !value || !value.val) {
		return '';
	}
	return valueInnerToString(value.val, users, products, companies, campaigns, noDateFormatting);
}

// --------------[CELLS]------------- //

const EmptyCell: Value = { tag: 15, val: null };

const LinkDiv = styled.div`
	cursor: pointer;
	width: 100%;
`;

/// A table shrinks to its content, so inside a centred box its cells have no
/// room to sit anywhere: aligning a figure to the right of a cell that is
/// exactly as wide as the figure moves nothing. It spans the card instead, and
/// the card keeps sizing itself to whatever is widest in it.
const ToolTipTable = styled.table`
	width: 100%;
`;

const ToolTipTd = styled.td<{ noBorder?: boolean, alignment?: Justify }>`
	vertical-align: middle;
	text-align: ${p => p.alignment === 'flex-end' ? 'right' : p.alignment ?? 'center'};
	${p => p.noBorder ? 'border: none !important;' : ''}
`;

export function onLinkClick(link: Link | null, links: ReportInterpretorProviderProps): void {
	if (!link) return;
	if ('clientCompany' in link) {
		const value = link.clientCompany;
		links.onCompanyClick(value);
	} else if ('contact' in link) {
		const value = link.contact;
		window.open(`contacts/detail/${value}`);
	} else if ('order' in link) {
		const value = link.order;
		window.open(`orders?id=${value}`);
	} else if ('product' in link) {
		const value = link.product;
		window.open(`products-v2?id=${value}`);
	} else if ('assortment' in link) {
		const value = link.assortment;
		window.open(`assortments-v2?id=${value}`);
	} else if ('shelfAudit' in link) {
		const value = link.shelfAudit;
		window.open(`enform/detaildata/${value}`);
	} else if ('user' in link) {
		console.warn('TODO');
	} else if ('formInstance' in link) {
		links.onFormInstanceClick(link.formInstance);
	} else {
		console.warn('unimplemented link :', link);
	}
}

function ReportLink(props: { link: Link | null, value: React.ReactNode }): JSX.Element {
	const { link, value } = props;
	const { onCompanyClick, onFormInstanceClick } = React.useContext(ReportInterpretorContext);

	return <LinkDiv onClick={() => onLinkClick(link, { onCompanyClick, onFormInstanceClick })}>{value}</LinkDiv>;
}

/// The value a tooltip reference points at. Kept apart from rendering it so
/// the cell around it can be aligned the way the table aligns the same value.
function referenceToValue(reference: Reference, primaryCell: Cell | undefined, cell: ComplexCell | Cell): Value | undefined {
	if (reference.val && 'values' in cell && 'complexValue' in reference.val) {
		// ComplexValue
		return cell.values[reference.val.complexValue];
	} else if (reference.tag === 0 && primaryCell) {
		// PrimaryCell
		return primaryCell.value;
	} else if (reference.tag === 1) {
		// PrimaryValue
		return cell.value;
	} else if (reference.val && 'primaryValueTupple' in reference.val && cell.value.val && 'evolution' in cell.value.val) {
		switch (reference.val.primaryValueTupple) {
			case 0: return cell.value.val.evolution.old;
			case 1: return cell.value.val.evolution.new;
		}
	}
	return undefined;
}

function referenceToNode(reference: Reference, primaryCell: Cell | undefined, cell: ComplexCell | Cell): React.ReactNode {
	const value = referenceToValue(reference, primaryCell, cell);
	return value === undefined ? undefined : <ValueToNode value={value} />;
}

function CellToNode(props: { cell: ComplexCell | Cell | undefined, isEmptyTile?: boolean, columnIndex?: number, color?: string, primary?: boolean, fontSize?: string, goal?: boolean, formatDuration?: boolean, valueColor?: ValueColor }): JSX.Element {
	const { isEmptyTile, columnIndex } = props;
	const cell = props.cell ?? { value: EmptyCell, link: null, tooltip: null };
	const { justify, className } = styleValue(cell.value);
	const value: React.ReactNode = <div style={{ width: '100%', display: 'flex', justifyContent: justify }} className={className}>{<ValueToNode value={cell.value} emptyTile={isEmptyTile} columnIndex={columnIndex} primary={props.primary} tag={cell.value.tag} goal={props.goal} formatDuration={props.formatDuration} valueColor={props.valueColor} />}</div>;
	let link: Link | null | undefined = cell.link?.val;

	if (cell.value.val) {
		if ('user' in cell.value.val) {
			link = { user: cell.value.val.user };
		}
		else if ('company' in cell.value.val) {
			link = { clientCompany: cell.value.val.company };
		}
		else if ('contact' in cell.value.val) {
			link = { contact: cell.value.val.contact };
		}
	}

	if (!link) {
		return <FlexDiv height='100%' justify={justify} align='center' width='100%' fontSize={props.fontSize}
			color={props.color}>{value}</FlexDiv>;
	}

	return <FlexDiv height='100%' justify={justify} align='center' width='100%'>
		<ReportLink link={link} value={value} />
	</FlexDiv>;
}

// --------------[TABLE]------------- //

const sort = (primaryLength: number, index: number, evolutionStateRef: React.MutableRefObject<EvolutionState>, users: Owner[], products: Product[], companies: Record<number, string>, campaigns: Record<number, Campaign>, statuses: Record<number, Status>, ctx: ReportInterpretorContextType) => (rowA: { original: ReactTableRow }, rowB: { original: ReactTableRow }, _columnId, desc: boolean) => {
	const translate = getTranslate(storeLang.getState().localize);
	let ra: Value;
	let rb: Value;
	if (index >= primaryLength) {
		ra = rowA.original.cells[index - primaryLength]?.value ?? EmptyCell;
		rb = rowB.original.cells[index - primaryLength]?.value ?? EmptyCell;
	} else {
		ra = rowA.original.primaryCell[index]?.value ?? EmptyCell;
		rb = rowB.original.primaryCell[index]?.value ?? EmptyCell;
	}

	if ((typeof ra === 'string' && typeof rb === 'string') || ((!ra || !ra.val) && (!rb || !rb.val))) return 0;
	if (typeof ra === 'string' || !ra || !ra.val) return desc ? -1 : 1;
	if (typeof rb === 'string' || !rb || !rb.val) return desc ? 1 : -1;

	let fra: number | undefined = undefined;
	let frb: number | undefined = undefined;
	if ('percentage' in ra.val) {
		fra = fractionToPercentage(ra.val.percentage);
	} else if ('float' in ra.val) {
		fra = ra.val.float;
	} else if ('int' in ra.val) {
		fra = Number(ra.val.int);
	} else if ('evolution' in ra.val) {
		if (evolutionStateRef.current[index] === '+') fra = calcEvolution(ra.val.evolution)?.flat;
		else {
			const evo = calcEvolution(ra.val.evolution);
			if (evo === undefined) fra = undefined;
			else {
				if (isNaN(evo.percentage)) {
					if (evo.flat === 0) fra = undefined;
					else fra = Infinity * evo.flat;
				} else fra = evo.percentage;
			}
		}
	}

	if ('percentage' in rb.val) {
		frb = fractionToPercentage(rb.val.percentage);
	} else if ('float' in rb.val) {
		frb = rb.val.float;
	} else if ('int' in rb.val) {
		frb = Number(rb.val.int);
	} else if ('evolution' in rb.val) {
		if (evolutionStateRef.current[index] === '+') frb = calcEvolution(rb.val.evolution)?.flat;
		else {
			const evo = calcEvolution(rb.val.evolution);
			if (evo === undefined) frb = undefined;
			else {
				if (isNaN(evo.percentage)) {
					if (evo.flat === 0) frb = undefined;
					else frb = Infinity * evo.flat;
				} else frb = evo.percentage;
			}
		}
	}
	if (fra && (!isFinite(fra) || isNaN(fra))) return desc ? 1 : -1;
	if (frb && (!isFinite(frb) || isNaN(frb))) return desc ? -1 : 1;

	if (fra !== undefined && frb !== undefined) return fra - frb;
	if (fra === undefined && frb !== undefined) return desc ? -1 : 1;
	if (frb === undefined && fra !== undefined) return desc ? 1 : -1;

	if ('colorDot' in ra.val && 'colorDot' in rb.val) return ra.val.colorDot.localeCompare(rb.val.colorDot);
	let tra: string | undefined = undefined;
	let trb: string | undefined = undefined;
	if ('text' in ra.val) {
		tra = ra.val.text;
	} else if ('key' in ra.val) {
		tra = translate(ra.val.key).toString();
	} else if ('formattedKey' in ra.val) {
		trb = translate(ra.val.formattedKey.key).toString();
		ra.val.formattedKey.formats.forEach((replacementKey, i) => trb = trb?.replaceAll(`{{${i + 1}}}`, replacementKey));
	} else if ('user' in ra.val) {
		const id = ra.val.user;
		tra = ctx?.mapValues?.users?.[id] ?? users.find(o => o.id === id)?.name;
	} else if ('product' in ra.val) {
		const uuid = ra.val.product;
		tra = ctx?.mapValues?.products?.[uuid] ?? products.find(p => p.uuid === uuid)?.name;
	} else if ('company' in ra.val) {
		tra = ctx?.mapValues?.companies?.[ra.val.company] ?? companies[ra.val.company];
	} else if ('campaign' in ra.val) {
		tra = ctx?.mapValues?.campaigns?.[ra.val.campaign] ?? campaigns[ra.val.campaign]?.name;
	} else if ('status' in ra.val) {
		const status = statuses[ra.val.status];
		if (status.status_name) { tra = status.status_name; }
	}

	if ('text' in rb.val) {
		trb = rb.val.text;
	} else if ('key' in rb.val) {
		trb = translate(rb.val.key).toString();
	} else if ('formattedKey' in rb.val) {
		trb = translate(rb.val.formattedKey.key).toString();
		rb.val.formattedKey.formats.forEach((replacementKey, i) => trb = trb?.replaceAll(`{{${i + 1}}}`, replacementKey));
	} else if ('user' in rb.val) {
		const idb = rb.val.user;
		trb = ctx?.mapValues?.users?.[idb] ?? users.find(o => o.id === idb)?.name;
	} else if ('product' in rb.val) {
		const uuid = rb.val.product;
		trb = ctx?.mapValues?.products?.[uuid] ?? products.find(p => p.uuid === uuid)?.name;
	} else if ('company' in rb.val) {
		trb = ctx?.mapValues?.companies?.[rb.val.company] ?? companies[rb.val.company];
	} else if ('campaign' in rb.val) {
		trb = ctx?.mapValues?.campaigns?.[rb.val.campaign] ?? campaigns[rb.val.campaign]?.name;
	} else if ('status' in rb.val) {
		const status = statuses[rb.val.status];
		if (status.status_name) { trb = status.status_name; }
	}

	if (tra === undefined && trb !== undefined) return desc ? -1 : 1;
	if (trb === undefined && tra !== undefined) return desc ? 1 : -1;

	if (tra !== undefined && trb !== undefined) return tra.localeCompare(trb);

	return JSON.stringify(ra).localeCompare(JSON.stringify(rb));
};

export const defaultFilters: FilterResult = {
	values: {
		array: [],
		combinator: 'and'
	},
	formatted: undefined
};

function RenderTableCell(props: {
	data: ReactTableRow | ComplexCell,
	header: Header,
	toolTipTemplate: TooltipTemplate | null,
	index: number,
	primaryLength: number,
	emptyTile?: boolean,
	valueColor?: ValueColor,
}) {
	let formatDuration = false;
	if (props.header) {
		const headerValues = props.header.cell.value.val && Object.values(props.header.cell.value.val);
		formatDuration = !!(props.header.cell.value.val && headerValues?.length && typeof headerValues[0] == 'string' && headerValues[0].includes('hh:mm:ss'));
	}
	let element: React.ReactNode = <></>;
	if (!('primaryCell' in props.data)) {
		element = <CellToNode cell={props.data} isEmptyTile={props.emptyTile} columnIndex={props.index} formatDuration={formatDuration} valueColor={props.valueColor} />;
	} else if (props.index < props.primaryLength) {
		element = <CellToNode cell={props.data.primaryCell[props.index]} isEmptyTile={props.emptyTile} columnIndex={props.index} primary />;
	} else {
		element = props.data.cells?.[props.index - props.primaryLength] ? <CellToNode cell={props.data.cells[props.index - props.primaryLength]} isEmptyTile={props.emptyTile} columnIndex={props.index} formatDuration={formatDuration} valueColor={props.valueColor} /> : undefined;
	}
	return <div style={{ height: '100%', cursor: 'pointer' }}>
		{element}
	</div>;
}

export type ToolTipHover = {
	cell: Cell | ComplexCell,
	bounds: DOMRect,
	toolTipTemplate: TooltipTemplate | null,
	primaryCell?: Cell,
	pos: tooltipPos
};

type ReportInterpretorProps = {
	setToolBarState?: (a) => void,
	report: Report,
	reportId?: number,
	height: number,
	filterParams?: FilterParameter[] | FilterParameterParent[],
	visualizationFitlerParams?: FilterParameter[] | FilterParameterParent[],
	filterResult?: FilterResult,
	onFiltersChange?: (filters: FilterResult) => void,
	reprasentation?: GenericReport,
	onStoreColumn?: (columnIndex: number, panelIndex: number, columnName: string) => void,
	onUnstoreColumn?: (columnId: number) => void,
	onCreateCalcField?: (columnIndex: number, panelIndex: number, columnName: string) => void,
	// the definition whose column colours the cells take, when it is not
	// the one shown: the editor's preview colours by the definition being
	// edited, as it changes
	columnColorsFrom?: GenericReport,
	externalActiveFilter?: number,
	context?: boolean,
	goalsProps?: goalsProps
	// fetches the real children of a lazily truncated row (see the report
	// query's subtree_of) and splices them into the report state. The row is
	// addressed by panel index plus the id path from the root row, because
	// row ids restart in every panel and only a full-path match guarantees
	// the server resolved the same node the user expanded.
	onLoadSubtree?: (panel: number, path: number[]) => Promise<void>,
};

export default function ReportInterpretor(props: ReportInterpretorProps & ReportInterpretorProviderProps) {
	return <ReportInterpretorProvider {...props}>
		<ReportInterpretorNoProvider {...props} />
	</ReportInterpretorProvider>;
}

export const OnFocusInputContainer = styled.div<{ isFocused: boolean, left?: string, top?: string, width?: string }>`
	${DefaultTextBlack}
	position: relative;
	z-index: 1;
	top: -50%;
	left: 100%;
	translate: 0 -50%;
	background-color: ${SurfaceColor};
	width: ${p => p.width ?? '180px'};
	display: ${p => p.isFocused ? 'flex' : 'none'};
	flex-direction: column;
	text-align: left;
	border-radius: 5px;
`;

export const InlineButton = styled.div<{ minHeight?: string, backgroundColor?: string }>`
	display: flex;
	align-items: center;
	min-height: ${p => p.minHeight ?? '25px'};
	border-bottom: 1px solid ${BorderColor};
	padding-left: 8px;
	background-color: ${p => p.backgroundColor ?? SurfaceColor};
	cursor: pointer;
	&:hover {
		background-color: color-mix(in oklab, ${PrimaryColor} 20%, transparent);
	}
`;

const StyledInput = styled.input<{ width?: string }>`
	${DefaultInput}
	height: 100%;
	width: ${p => p.width ?? '80px'};
	text-align: center;
	-moz-appearance: textfield;
	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
`;

const GoalInputCell = ({ onBlur, onSetAllColumn, onSetAllColumnCustom, value, isInputDisabled }: { onBlur: (value: string) => void, onSetAllColumn: (value: string) => void, onSetAllColumnCustom: (value: string) => void, value: string, isInputDisabled: boolean }) => {
	const [inputValue, setInputValue] = React.useState(value);
	const [isFocused, setIsFocused] = React.useState(false);
	React.useEffect(() => {
		setInputValue(value);
	}, [value]);
	return <div style={{ width: '100%', height: '100%' }} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}>
		<FlexDiv justify='center' width='100%' height='100%'>
			<StyledInput
				type="number"
				disabled={isInputDisabled}
				value={inputValue}
				onChange={(e) => setInputValue(e.target.value)}
				onBlur={() => onBlur(inputValue)}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') {
						(e.target as HTMLInputElement).blur();
					}
				}}
			/>
		</FlexDiv>
		<OnFocusInputContainer isFocused={isFocused && !isInputDisabled}>
			<InlineButton onMouseDown={() => onSetAllColumn(inputValue)}>
				<Translate id='report.apply_to_column' />
			</InlineButton>
			<InlineButton onMouseDown={() => onSetAllColumnCustom(inputValue)}>
				<Translate id='report.apply_last_period' />
			</InlineButton>
		</OnFocusInputContainer>
	</div>;
};

const DashboardCheckbox = ({ isCheckboxDisabled, checked, onChange, onCheckAll }: { isCheckboxDisabled: boolean, checked: boolean, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onCheckAll: () => void }) => {
	const [isHovered, setIsHovered] = React.useState(false);

	return <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{ height: '100%', width: '100%' }}>
		<FlexDiv justify='center' style={{ height: '100%', width: '100%' }}>
			<BoolCheckbox type='checkbox'
				style={{ backgroundColor: isCheckboxDisabled ? BorderColor : SurfaceColor, cursor: isCheckboxDisabled ? 'not-allowed' : 'pointer' }}
				checked={checked}
				title={isCheckboxDisabled ? 'Enter a goal to enable adding goals to the dashboard' : ''}
				disabled={isCheckboxDisabled}
				onChange={onChange}
			/>
		</FlexDiv>
		<OnFocusInputContainer isFocused={isHovered && !isCheckboxDisabled} left='30px' top='-22px' width='110px'>
			<InlineButton onMouseDown={() => onCheckAll()}>
				<Translate id='report.apply_to_column' />
			</InlineButton>
		</OnFocusInputContainer>
	</div>;
};

export function goalPathHash(obj: object | undefined | null): string {
	function sortObject(o: object): any {
		if (o === null || typeof o !== 'object') {
			return o;
		}
		if (Array.isArray(o)) {
			return o.map(sortObject);
		}
		const sortedKeys = Object.keys(o).sort();
		const result: object = {};
		sortedKeys.forEach(key => {
			result[key] = sortObject(o[key]);
		});
		return result;
	}
	if (!obj) {
		return '';
	}
	return JSON.stringify(sortObject(obj));
}

function getPathFromRowWithDate(row: ReactTableRow, formattedDate: string): GoalPath {
	return ({ ...row.path, date: formattedDate } as GoalPath);
}

function readPinnedColumns(reportKey: string): number {
	return getLocalStorage<Record<string, number>>('report_pinned_columns')?.[reportKey] ?? 3;
}

export function ReportInterpretorNoProvider(props: ReportInterpretorProps) {
	const [selectedPanel, setSelectedPanel] = React.useState(0);
	// the expander Cell below is memoized by react-table with first-render
	// props: everything it needs live at click time goes through this ref
	const lazyLoadRef = React.useRef({ selectedPanel, onLoadSubtree: props.onLoadSubtree });
	lazyLoadRef.current.selectedPanel = selectedPanel;
	lazyLoadRef.current.onLoadSubtree = props.onLoadSubtree;
	const [selectedHeader, setSelectedHeader] = React.useState<number>();
	const [selectedView, setSelectedView] = React.useState<'table' | 'chart'>('table');
	const [selectedCheckbox, setSelectedCheckbox] = React.useState<boolean[]>([]);
	const pinnedColumnsKey = `${props.reportId ?? 0}`;
	const [pinnedUntil, setPinnedUntil] = React.useState<number>(() => readPinnedColumns(pinnedColumnsKey));
	React.useEffect(() => setPinnedUntil(readPinnedColumns(pinnedColumnsKey)), [pinnedColumnsKey]);
	const pinColumnsUntil = React.useCallback((count: number) => {
		setPinnedUntil(count);
		setLocalStorage('report_pinned_columns', { ...getLocalStorage<Record<string, number>>('report_pinned_columns'), [pinnedColumnsKey]: count });
	}, [pinnedColumnsKey]);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const { evolutionStateRef, onCompanyClick, onFormInstanceClick } = React.useContext(ReportInterpretorContext);
	const users = useRecoilValue(AUsers);
	const products = useRecoilValue(AProducts);
	const companies = useRecoilValue(ACompanies);
	const campaigns = useRecoilValue(ACampaigns);
	const statuses = useRecoilValue(AStatuses);
	const ctx = React.useContext(ReportInterpretorContext);
	const [hidden, setHidden] = React.useState<boolean>(false);
	const [, setTagsFilter] = useRecoilState(ATagFilter);
	const additionalColumns = useRecoilValue(AAdditionalColumns);
	const isAdmin = isSuperAdmin();
	let meOwner;
	try {
		const me = useMe();
		meOwner = isOwner(me);
	} catch (_e) {
		meOwner = false;
	}
	// Filters
	const [isFilterOpen, setFilterOpen] = React.useState<boolean>(false);
	const [isVizualisationFilterOpen, setVizualisationFilterOpen] = React.useState<boolean>(false);
	const [filterResult, setFilterResult] = useFunctionState<FilterResult>(props.filterResult ?? defaultFilters, ({ oldValue, newValue }) => {
		if (oldValue.formatted === newValue.formatted) {
			return oldValue;
		}
		props.onFiltersChange?.(newValue);
		return newValue;
	});
	const [vizualisationFilterResult, setVizualisationFilterResult] = React.useState<FilterResult>(defaultFilters);
	const [isOpenSummary, setOpenSummary] = React.useState<boolean>(false);
	const [reportGranularity, setReportGranularity] = React.useState<string>('month');
	const { filters, setFilters } = React.useContext(ReportFiltersContext);

	const [nbFilters, setNbFilters] = React.useState<number>(filtersLenght(filters));
	const [goals, setGoals] = React.useState<Goal[]>(props.goalsProps?.goals ?? []);
	const [filterGoals, setFilterGoals] = React.useState<string | undefined>(undefined);
	const selectedPanelName = props.report.panels.map((panel) => panel.value.val && 'text' in panel.value.val ? panel.value.val.text : '')[selectedPanel];
	const groupingHeadersLength = props.report.panels[selectedPanel]?.headers.filter(h => h.cell.value.val && !('date' in h.cell.value.val)).length ?? 0;
	const pickedDates = useRecoilState(ADatePicker)[0];
	const showDelta = props.goalsProps?.showDelta ?? false;
	const weightByRef = props.goalsProps?.weightByRef ?? false;

	React.useEffect(() => {
		setNbFilters(filtersLenght(filters));
	}, [filters]);

	React.useEffect(() => {
		setGoals(props.goalsProps?.goals ?? []);
	}, [props.goalsProps?.goals]);

	const goalPathsHashMap: GoalHashMap = React.useMemo(() => {
		const res = new Map<string, { value: string, index: number, dashboard: boolean, evolution: Cell | ComplexCell | undefined }>();
		goals?.forEach((goal, goalIndex) => {
			if (Object.keys(goal.path).length !== groupingHeadersLength || goal.granularity !== reportGranularity || (goal.column_name && selectedPanelName !== goal.column_name)) return;
			goal.goals.forEach(g => {
				const date = getStartOfGranularity(goal.granularity as Granularity, moment(g.date));
				const path = { 'date': date.format('YYYY-MM-DD'), ...goal.path };
				res.set(goalPathHash(path), { value: g.value.toString(), index: goalIndex, dashboard: g.dashboard, evolution: undefined });
			});
		});
		return res;
	}, [reportGranularity, selectedPanelName, groupingHeadersLength, goals]);

	function filtersLenght(filters: LocalFilters): number {
		let i = 0;
		if (filters) {
			if (filters.form_filter && (filters.form_filter as number[]).length > 0)
				i = i + 1;
			if (filters.created_by_filter && (filters.created_by_filter as number[]).length != users.length)
				i = i + 1;
			if (filters.my_filters) {
				if ((filters.my_filters).products && !(filters.my_filters).products?.all)
					i = i + 1;
				if ((filters.my_filters).brands && !(filters.my_filters).brands?.all)
					i = i + 1;

				if ((filters.my_filters).tags && (filters.my_filters).tags!.length > 0)
					i = i + 1;
			}
		}
		return i;
	}

	const { report: r } = props;
	const height = props.height - (r.panels.length > 1 ? PANEL_SELECTOR_HEIGHT : 0);
	let report: Panel | undefined = undefined;
	if (r.panels[selectedPanel]) report = r.panels[selectedPanel];
	const alert = useAlert();
	const colors = React.useMemo(() => {
		const table: number[] = [];
		const flattenRowIds = (row: Row) => {
			table.push(row.id);
			row.rows.forEach(flattenRowIds);
		};
		report?.rows.forEach(flattenRowIds);
		return {
			rowColors: createRandomColor(table),
			panelColors: createRandomColor(r.panels)
		};
	}, [report]);


	React.useEffect(() => {
		if (props.filterResult) {
			setFilterResult(props.filterResult);
		}
	}, [props.filterResult]);

	function Visibility(propos: { defaultValue: boolean, onChange: (b: boolean) => void }) {
		const [hidden, setHidden] = React.useState<boolean>(propos.defaultValue);
		let icon = VisibilityOnEmptyIcon;
		if (selectedCheckbox.some(b => b === true)) {
			if (hidden) {
				icon = VisibilityOffIcon;
			}
			else {
				icon = VisibilityOnIcon;
			}
		}
		else {
			if (hidden) {
				setHidden(false);
				alert({
					title: 'Attention',
					content: 'Veuillez sélectionner au moins une entreprise',
					width: '475px',
					noButtons: true,
					svg: 'warning'
				});
				propos.onChange(false);
				icon = VisibilityOffIcon;
			}
		}
		return (<><Image onClick={() => {
			if (selectedCheckbox.some(b => b === true)) {
				setHidden(!hidden); propos.onChange(!hidden);
			}
			else {
				alert({
					title: 'Attention',
					content: 'Veuillez sélectionner au moins une entreprise',
					width: '475px',
					noButtons: true,
					svg: 'warning'
				});
			}
		}} url={icon} width='20px' /></>);
	}

	function isColAverage(row: ReactTableRow, columnIndex: number): boolean {
		let isAverage = false;
		let valueFound = false;
		function getAllCellValuesRecursively(row: ReactTableRow): void {
			if (valueFound || !row.subRows || row.subRows.length === 0) {
				if (columnIndex < row.cells.length) {
					const cell = row.cells[columnIndex].value;
					if (cell && cell.val) {
						if ('percentage' in cell.val || 'average' in cell.val) {
							isAverage = true;
							valueFound = true;
						}
						else if ('float' in cell.val || 'int' in cell.val) {
							valueFound = true;
						}
					}
				}
			}
			else
				row.subRows.forEach(child => getAllCellValuesRecursively(child));
		}
		getAllCellValuesRecursively(row);
		return valueFound ? isAverage : true;
	}

	function getAverageValueForDate(date: string, leafLen: number): { num: number, denom: number } | undefined {
		let numOfGoals = 0;
		const sum = goals.reduce((acc, goal: Goal) => {
			if (goal.goals && goal.granularity === reportGranularity && Object.keys(goal.path).length - 1 === leafLen && (!goal.column_name || !goal.column_name.length || selectedPanelName === goal.column_name)) {
				const goalForDate = goal.goals.find((goal) => goal.date === date);
				if (goalForDate) {
					const goalForDateVal = goalForDate.value;
					if (goalForDateVal && !isNaN(goalForDateVal)) {
						numOfGoals++;
						return goalForDateVal + acc;
					}
				}
			}
			return acc;
		}, 0);
		return sum && numOfGoals ? { num: sum, denom: numOfGoals } : undefined;
	}

	function getLeafGoalWeight(row: ReactTableRow, cellIndex: number): number {
		if (!weightByRef || cellIndex < 0 || cellIndex >= row.cells.length) return 1;
		const cell = row.cells[cellIndex].value;
		if (cell.tag === 0 && cell.val.percentage.denom) return cell.val.percentage.denom;
		return 1;
	}

	function getFinalChildrenGoalsRecursively(row: ReactTableRow, siblingGoalValues: { value: number, weight: number }[], formattedDate: string, cellIndex: number): void {
		if (!row.subRows || row.subRows.length === 0) {
			const goalValue = goalPathsHashMap.get(goalPathHash(getPathFromRowWithDate(row, formattedDate)));
			if (goalValue && goalValue.value && goalValue.value.length > 0) {
				const value = parseFloat(goalValue.value);
				if (!isNaN(value))
					siblingGoalValues.push({ value, weight: getLeafGoalWeight(row, cellIndex) });
			}
		}
		else
			row.subRows.forEach(sibling => getFinalChildrenGoalsRecursively(sibling, siblingGoalValues, formattedDate, cellIndex));
	}

	function getChildrenGoalSum(row: ReactTableRow, formattedDate: string, cellIndex: number): { num: number, denom: number, sum: number } | undefined {
		const siblingGoalValues: { value: number, weight: number }[] = [];
		getFinalChildrenGoalsRecursively(row, siblingGoalValues, formattedDate, cellIndex);
		if (siblingGoalValues.length === 0) return undefined;
		return siblingGoalValues.reduce((acc, v) => ({ num: acc.num + v.value * v.weight, denom: acc.denom + v.weight, sum: acc.sum + v.value }), { num: 0, denom: 0, sum: 0 });
	}

	function getChildrenGoalValue(row: ReactTableRow, formattedDate: string, cellIndex: number, isAverage: boolean): number | undefined {
		const sum = getChildrenGoalSum(row, formattedDate, cellIndex);
		if (!sum) return undefined;
		return parseFloat((isAverage ? sum.num / sum.denom : sum.sum).toFixed(2));
	}

	function getCellValue(cell: Value, emptyGoalInterpretation: string): number | null {
		let cellValue: number | null;
		switch (cell.tag) {
			case 0:
				cellValue = cell.val.percentage.numer / cell.val.percentage.denom * 100;
				break;
			case 4:
				cellValue = cell.val.float;
				break;
			case 5:
				cellValue = cell.val.int;
				break;
			case 17:
				cellValue = cell.val.average.numerator / cell.val.average.denominator;
				break;
			default:
				cellValue = emptyGoalInterpretation === 'null' ? null : 0;
				break;
		}
		return cellValue;
	}

	function getSumValueOfChildrenWithZeros(row: ReactTableRow, formattedDate: string, cellIndex: number): number | null {
		const allValues: number[] = [];
		function getAllCellValuesRecursively(row: ReactTableRow): void {
			if (!row.subRows || row.subRows.length === 0) {
				const currentPathHash = goalPathHash(getPathFromRowWithDate(row, formattedDate));
				const goalValue = goalPathsHashMap.get(currentPathHash);
				if (goalValue && goalValue.value && goalValue.value.length > 0 && cellIndex < row.cells.length) {
					const cell = row.cells[cellIndex].value;
					const cellValue = getCellValue(cell, 'null');
					if (cellValue !== null)
						allValues.push(cellValue);
				}
			}
			else
				row.subRows.forEach(child => getAllCellValuesRecursively(child));
		}
		getAllCellValuesRecursively(row);
		return allValues.reduce((acc, val) => acc + val, 0);
	}

	function getAverageCellValueOfChildrenWithZeros(row: ReactTableRow, formattedDate: string, cellIndex: number, countValuesWithoutGoals?: boolean): number | null {
		const allValues: { numer: number, denom: number }[] = [];
		let multiplicator = 1;
		function getAllCellValuesRecursively(row: ReactTableRow): void {
			if (!row.subRows || row.subRows.length === 0) {
				const currentPathHash = goalPathHash(getPathFromRowWithDate(row, formattedDate));
				const goalValue = goalPathsHashMap.get(currentPathHash);
				if ((countValuesWithoutGoals || (goalValue && goalValue.value && goalValue.value.length > 0)) && cellIndex < row.cells.length) {
					const cell = row.cells[cellIndex].value;
					let cellValue;
					switch (cell.tag) {
						case 0:
							multiplicator = 100;
							cellValue = { numer: cell.val.percentage.numer, denom: cell.val.percentage.denom };
							break;
						case 17:
							cellValue = { numer: cell.val.average.numerator, denom: cell.val.average.denominator };
							break;
						default:
							cellValue = { numer: 0, denom: 0 };
							break;
					}
					if (cellValue !== null)
						allValues.push(cellValue);
				}
			}
			else
				row.subRows.forEach(child => getAllCellValuesRecursively(child));
		}
		getAllCellValuesRecursively(row);
		const allValuesSum = allValues.reduce((acc, val) => ({ numer: acc.numer + val.numer, denom: acc.denom + val.denom }), { numer: 0, denom: 0 });
		return allValuesSum.denom ? allValuesSum.numer / allValuesSum.denom * multiplicator : null;
	}

	function updateOneGoal(formattedDate: string, trimmedInput: string, path: GoalPath, goalValue: { value: string; index: number; dashboard: boolean } | undefined, newGoals: Goal[]): Goal & { deleted: boolean } | undefined {
		let updatedGoal: Goal & { deleted: boolean } | undefined;
		const pathWithoutDate = Object.keys(path).reduce((acc, key) => {
			if (key !== 'date') {
				acc[key] = path[key];
			}
			return acc;
		}, {});
		const inputToFloat = parseFloat(trimmedInput);
		if (_.isEmpty(pathWithoutDate)) return;
		if (goalValue?.index !== undefined && goalValue.index >= 0 && newGoals[goalValue.index]) {
			const goalToUpdate = _.cloneDeep(newGoals[goalValue.index]);
			if (!trimmedInput.length) { // Delete goal
				updatedGoal = { ...goalToUpdate, goals: goalToUpdate.goals.filter(g => g.date === formattedDate), deleted: true };
				newGoals[goalValue.index].goals = newGoals[goalValue.index].goals.filter(g => g.date !== formattedDate);
			}
			else { // Update goal
				newGoals[goalValue.index].goals = newGoals[goalValue.index].goals.map(g => {
					if (g.date === formattedDate) {
						const updatedGoalGoal = {
							...g,
							value: inputToFloat,
							date: formattedDate,
						};
						updatedGoal = { ...goalToUpdate, goals: [updatedGoalGoal], deleted: false };
						return updatedGoalGoal;
					}
					return g;
				});
			}
		}
		else {
			if (trimmedInput.length > 0) {
				const pathStringified = goalPathHash(pathWithoutDate);
				const newGoalPath = newGoals.find(g => goalPathHash(g.path) === pathStringified && g.granularity === reportGranularity && (!g.column_name || g.column_name === selectedPanelName));
				if (newGoalPath) { // Update goal new column
					const updatedGoalGoal = { value: inputToFloat, date: formattedDate, dashboard: goalValue?.dashboard ?? false };
					updatedGoal = { ...newGoalPath, goals: [updatedGoalGoal], deleted: false };
					newGoalPath.goals.push(updatedGoalGoal);
				}
				else { // Create goal
					const newGoal = {
						report_id: props.reportId ?? 0,
						path: pathWithoutDate,
						column_name: selectedPanelName,
						granularity: reportGranularity,
						goals: [{ value: inputToFloat, date: formattedDate, dashboard: goalValue?.dashboard ?? false }]
					};
					updatedGoal = { ...newGoal, deleted: false };
					newGoals.push(newGoal);
				}
			}
		}
		return updatedGoal;
	}

	function setAllColumnGoals(values: string[], row: ReactTableRow, formattedDate: string, reportHeadersNoDate: Header[], newGoals: Goal[], goalsToUpdate: Goal[], goalsToDelete: GoalDelete[]): void {
		if (!row.parent)
			return;
		const siblingPaths: string[] = [];
		const activeRowPath = getPathFromRowWithDate(row.parent, formattedDate);
		row.parent.subRows.forEach((sibling, i) => {
			const siblingPathValue = sibling.primaryCell[reportHeadersNoDate.length - 1].value.val;
			const objectTitle = Object.values(reportHeadersNoDate[reportHeadersNoDate.length - 1].cell.value.val ?? {})[0];
			let totalPath;
			if (siblingPathValue) {
				totalPath = {
					...activeRowPath, [objectTitle]: {
						'value': siblingPathValue,
						'index': reportHeadersNoDate.length - 1
					}
				};
				siblingPaths.push(goalPathHash(totalPath));
			}
			const siblingGoalValue = goalPathsHashMap.get(goalPathHash(totalPath));
			const updatedGoal = updateOneGoal(formattedDate, values.length > i ? values[i] : values[0], totalPath, siblingGoalValue, newGoals);
			if (updatedGoal) {
				if (updatedGoal.deleted) {
					goalsToDelete.push({ ...updatedGoal, date: updatedGoal.goals[0].date });
				}
				else {
					goalsToUpdate.push(updatedGoal);
				}
			}
		});
	}

	function updateOneDashboard(goalValue: { value: string; index: number; dashboard: boolean } | undefined, isDashboard: boolean, formattedDate: string, newGoals: Goal[]): Goal | undefined {
		if (!goalValue) return;
		let updatedGoal: Goal | undefined;
		newGoals[goalValue.index].goals = newGoals[goalValue.index].goals.map(g => {
			if (g.date === formattedDate) {
				const goalGoal = {
					...g,
					dashboard: isDashboard
				};
				updatedGoal = { ...newGoals[goalValue.index], goals: [goalGoal] };
				return goalGoal;
			}
			return g;
		});
		return updatedGoal;
	}

	function updateDashboardRecursively(row: ReactTableRow, isDashboard: boolean, formattedDate: string, newGoals: Goal[], goalsToUpdate: Goal[]) {
		if (row.subRows && row.subRows.length > 0) {
			row.subRows.forEach(child => updateDashboardRecursively(child, isDashboard, formattedDate, newGoals, goalsToUpdate));
			return;
		}
		const goalToUpdate = updateOneDashboard(goalPathsHashMap.get(goalPathHash(getPathFromRowWithDate(row, formattedDate))), isDashboard, formattedDate, newGoals);
		if (goalToUpdate)
			goalsToUpdate.push(goalToUpdate);
	}

	function updateAllDashboard(row: ReactTableRow, isDashboard: boolean, formattedDate: string, newGoals: Goal[], goalsToUpdate: Goal[]) {
		if (!row.parent)
			return;
		row.parent.subRows.forEach(sibling => updateDashboardRecursively(sibling, isDashboard, formattedDate, newGoals, goalsToUpdate));
	}

	function areNoChildrenSet(row: ReactTableRow, formattedDate: string): boolean {
		if (!row.subRows || row.subRows.length === 0) {
			const currentPathHash = goalPathHash(getPathFromRowWithDate(row, formattedDate));
			return !goalPathsHashMap.has(currentPathHash);
		}
		return row.subRows.every(child => areNoChildrenSet(child, formattedDate));
	}

	function areAllChildrenSetDashboard(row: ReactTableRow, formattedDate: string): boolean {
		if (!row.subRows || row.subRows.length === 0) {
			const currentPathHash = goalPathHash(getPathFromRowWithDate(row, formattedDate));
			const goalValue = goalPathsHashMap.get(currentPathHash);
			if (!goalValue) return true;
			return goalValue.dashboard;
		}
		return row.subRows.every(child => areAllChildrenSetDashboard(child, formattedDate));
	}

	function setEvolutionHashMap(cell: Cell | ComplexCell, path: GoalPath, goalFromHashMap: { value: string, index: number, dashboard: boolean, evolution: Cell | ComplexCell | undefined } | undefined): void {
		if (goalFromHashMap) {
			goalPathsHashMap.set(goalPathHash(path), { ...goalFromHashMap, evolution: cell });
		}
	}

	React.useEffect(() => {
		props.setToolBarState?.({
			bottomLeftToolbarComponent: <>
				{props.filterParams !== undefined && props.filterParams.length > 0 && !(props.reprasentation?.hidden_filters) &&
					<ToolbarFilterButton
						activeFilters={
							props.context ? (nbFilters + filterTreeLength(filterResult.formatted) + (props.externalActiveFilter ?? 0)) : filterTreeLength(filterResult.formatted) + (props.externalActiveFilter ?? 0)
						}
						onClick={() => {
							setOpenSummary(true);
							setFilterOpen(true);
						}}
						onDeleteFilter={() => {
							if (props.context) {
								setFilters({ ...filters, form_filter: undefined, created_by_filter: undefined, filter: undefined, my_filters: { ...(filters.my_filters), tags: undefined, products: undefined, brands: undefined } });
							}
							setTagsFilter([]);
							setFilterResult({ values: { combinator: 'and', array: [] }, formatted: undefined });
						}}
					/>}
			</>,
			bottomRightToolbarComponent: <>
				<FlexDiv gap='10px'>
					{props.visualizationFitlerParams !== undefined && props.visualizationFitlerParams?.length > 0 &&
						<ToolbarFilterButton
							color={SidelyBlack}
							title={translateToString('settings')}
							activeTitle={translateToString('settings') + ' ({{NUMBER}})'}
							icon={<Settings color={SidelyBlack} />}
							onClick={() => {
								setOpenSummary(true);
								setVizualisationFilterOpen(true);
							}}
							onDeleteFilter={() => {
								setVizualisationFilterResult({ values: { combinator: 'and', array: [] }, formatted: undefined });
							}}
						/>
					}
					<IconsGroupBackground background={true}>
						<SwapImageContainer selected={selectedView === 'table'} onClick={_ => setSelectedView('table')}>
							<Menu />
						</SwapImageContainer>
						<SwapImageContainer selected={selectedView === 'chart'} onClick={_ => {
							if (selectedCheckbox.some(b => b)) {
								setSelectedView('chart');
							} else {
								alert({
									title: 'Attention',
									content: <Translate id='select_at_least_one_company' />,
									width: '475px',
									noButtons: true,
									svg: 'warning'
								});
							}
						}}>
							<ChartImg />
						</SwapImageContainer>
					</IconsGroupBackground>
				</FlexDiv>
			</>
		});
	}, [selectedView, selectedCheckbox, filterResult, props.externalActiveFilter, props.visualizationFitlerParams, vizualisationFilterResult, nbFilters, props.reprasentation?.hidden_filters]);

	const chart = React.useMemo(() => report && reportToChart(r, report, selectedPanel, selectedCheckbox, colors, selectedHeader ?? 0, users, products, companies, campaigns), [selectedView, selectedPanel, report, selectedHeader]);
	const onTableLoad = React.useCallback(() => setLoadingState(LoadingStateEnum.LOADED), []);
	const reportHeaders = report ? report.headers.filter(h => h.cell.value.val && !('date' in h.cell.value.val)) : [];
	const datas = React.useMemo(() => report?.rows.filter(row_to_filter => {
		return selectedCheckbox[row_to_filter.id] || !hidden;
	}).reduce((acc: ReactTableRow[], row) => {
		const r = filterRow(toReactTableData(row, reportHeaders.length > 0 ? reportHeaders : undefined), vizualisationFilterResult.formatted, filterGoals, goalPathsHashMap);
		if (r) acc.push(r);
		return acc;
	}, []) ?? [], [report, hidden, vizualisationFilterResult, filterGoals]);

	const columns: Column<ReactTableRow>[] = React.useMemo(() => {
		let reprasentationIndex = 0;
		if (!report) return [];
		const primaryLength = report.headers.filter(h => h.primary).length;
		const rows = report.rows;
		const cols = report.headers.reduce((acc: Column<ReactTableRow>[], header, columnIndex): Column<ReactTableRow>[] => {
			const headerName = valueToString(header.cell.value);
			let calcFieldName = headerName ?? '';
			const toolTipTemplate = header.tooltip[selectedPanel];
			let options;
			const isEmptyTitle = header.cell.value.val === null;
			if (isEmptyTitle) {
				options = {
					maxWidth: 60,
					minWidth: 60,
					width: 60,
					oldResize: true
				};
			} else {
				const titleWidth = headerName ? getStringWidth(headerName, 500, 100, 8) + 30 : 250;
				options = {
					maxWidth: 500,
					minWidth: titleWidth,
					width: titleWidth,
					noResizer: report.headers[columnIndex + 1]?.cell.value.val === null
				};
			}

			const tamer = reprasentationIndex; // Js bad, closures capture by ref so we need a copy
			const isHidden = vizualisationFilterResult.formatted && 'and' in vizualisationFilterResult.formatted && vizualisationFilterResult.formatted.and.some(f => 'val' in f && f.val.column === (columnIndex - primaryLength).toString() && f.val.operator === 'not_empty');
			const columnOptionsData: DropdownData[] = [
				{ value: !isHidden ? 'hide_lines' : 'show_lines', label: !isHidden ? translateToString('reports.column_options.hide') : translateToString('reports.column_options.unhide') },
			];
			if (header.storeable && props.reprasentation?.group_by === 'Company' && meOwner) {
				// une colonne externe (ClickHouse) ne se duplique en champ
				// calculé qu'en agrégation Somme: le worker KPI ne sait que
				// sommer sur une fenêtre glissante
				const repColumn = props.reprasentation?.panels?.[0]?.columns?.[tamer] as unknown;
				const external = repColumn && typeof repColumn === 'object' && 'External' in repColumn
					? (repColumn as { External: { aggregation?: string } }).External : undefined;
				if (!external || (external.aggregation ?? 'Sum') === 'Sum') {
					columnOptionsData.push({ value: 'turn_to_calc_field', label: translateToString('reports.column_options.turn_to_calc_field') });
				}
			}
			// the colours set, in the editor, on the column of the definition
			// this header stands for
			const colored = props.columnColorsFrom ?? props.reprasentation;
			const column = header.storeable && !header.primary ? colored?.panels?.[0]?.columns?.[tamer] : undefined;
			const displayOptions = columnDisplayOptionsOf(colored, column);
			// the scale is read over the column's values in the rows shown
			let cellColor: ValueColor | undefined;
			if (displayOptions?.color_scale) {
				const shown = rangeOfValues(rows
					.map(row => scaleValueOf('ReportColumn', row.cells[columnIndex - primaryLength]?.value.val)?.value)
					.filter((v): v is number => v !== undefined));
				cellColor = value => valueColor(displayOptions, 'ReportColumn', value, shown);
			}
			const col: Column<ReactTableRow> = {
				id: `column[${columnIndex}]`,
				noPinOption: !header.primary,
				noHeaderEllipsis: true,
				Header: isEmptyTitle ? <EvolutionDropdown headerIndex={columnIndex} /> :
					<FlexDiv width='95%'>
						<DefaultImage width='16px' filter={FilterPrimaryColor} src={VisibilityOffIcon} display={!isHidden ? 'none' : 'block'} />
						<CellToNode color={isHidden ? PrimaryColor : ''} cell={header.cell} />
						{!header.primary &&
							<>
								<Dropdown
									stopPropagation
									searchable={false}
									name='ColumnOptions'
									dropdownStyle={{ optionWidth: '230px', optionLeft: '-205px', height: 'auto' }}
									datalist={columnOptionsData}
									JSXButton={() => <ToolbarImageContainer hasPointer rotate='90deg' marginLeft='-3px'><Ellipsis color={GreySidely3} strokeWidth={2.5} /></ToolbarImageContainer>}
									onChange={(value: DropdownData) => {
										switch (value.value) {
											case 'show_lines':
											case 'hide_lines': {
												const newT = vizualisationFilterResult.formatted ?? { and: [] };
												if ('and' in newT) {
													if (isHidden) {
														newT.and = newT.and.filter(f => !('val' in f && f.val.column === (columnIndex - primaryLength).toString() && f.val.operator === 'not_empty'));
													} else {
														newT.and.push({
															val: {
																column: (columnIndex - primaryLength).toString(),
																operator: 'not_empty'
															}
														});
													}
													setVizualisationFilterResult({ ...vizualisationFilterResult, formatted: { ...newT } });
												}
												break;
											}
											case 'turn_to_calc_field': alert({
												title: 'Attention',

												content: <FlexDiv flow='column' align='stretch' justify='center' gap='1.5rem' fontSize='14px' padding='10px 0 0 0'>
													<div>{translateToString('additional_create', [['headerName', headerName!]])}</div>
													<FlexDiv flow='column' align='stretch' gap='0.75rem'>
														<Translate id='calculated_fields.new_name' />
														<Input type='text' name='calcFieldName' value={calcFieldName} onChange={e => calcFieldName = e} inputStyle={{
															width: '365px',
															padding: '0 10px',
															borderRadius: '5px',
															fontSize: '14px',
														}} />
													</FlexDiv>
												</FlexDiv>,
												textAlign: 'center',
												width: '475px'
											}).then(r => {
												if (r.res == AlertRes.Ok) {

													props.onCreateCalcField?.(tamer, selectedPanel, calcFieldName.trim().length > 0 ? calcFieldName.trim() : headerName!);
												}
											}); break;
										}
									}}
								/>
							</>
						}
					</FlexDiv>,
				accessor: (row: ReactTableRow) => <RenderTableCell primaryLength={primaryLength} data={row} header={header} toolTipTemplate={toolTipTemplate} index={columnIndex} emptyTile={isEmptyTitle} valueColor={cellColor} />,
				Footer: report.footers?.[columnIndex] ? <RenderTableCell primaryLength={primaryLength} header={header} toolTipTemplate={toolTipTemplate} index={columnIndex} data={report.footers[columnIndex]} /> : <></>,
				sortType: sort(primaryLength, columnIndex, evolutionStateRef, users, products, companies, campaigns, statuses, ctx),
				toolTip: (row, type) => {
					const data = type == 'row' ? row : report.footers[columnIndex];
					if (!data) return;
					let cell: ComplexCell | Cell | undefined;
					let primaryCell: Cell | undefined;
					if ('primaryCell' in data && columnIndex < primaryLength) {
						cell = data.primaryCell[columnIndex];
						primaryCell = data.primaryCell[0];
					} if ('primaryCell' in data) {
						cell = data.cells?.[columnIndex - primaryLength] ? data.cells[columnIndex - primaryLength] : undefined;
						primaryCell = data.primaryCell.find(p => p.value.tag != EmptyCell.tag) ?? data.cells?.find(c => c.value.tag != EmptyCell.tag) ?? undefined;
					} else {
						cell = data;
						primaryCell = undefined;
					}
					if (!cell || cell.value.val === null) return undefined;
					return {
						cell,
						toolTipTemplate,
						primaryCell,
						pos: type === 'row' ? 'left' : 'up'
					};
				},
				...options
			};

			if (header.storeable) {
				reprasentationIndex++;
			}

			return [...acc, col];
		}, [
			{
				id: 'expander',
				noPinOption: true,
				Header: (props: unknown) => {
					//@ts-expect-error it works
					const { getToggleAllRowsExpandedProps, isAllRowsExpanded } = props;
					return (
						<span {...getToggleAllRowsExpandedProps({ title: undefined })} style={{ fontSize: '20px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
							{isAllRowsExpanded ? '-' : '+'}
						</span>
					);
				},
				Cell: (p: { row }) => {
					const row = p.row;
					if (!row.canExpand) return null;
					const toggleProps = row.getToggleRowExpandedProps({
						title: undefined,
						style: {
							fontSize: '20px',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							height: '100%'
						}
					});
					return (
						<span
							{...toggleProps}
							onClick={e => {
								// truncated node: fetch the real children while
								// the sentinel "loading" row shows
								if (row.original.lazy && !row.isExpanded) {
									const path: number[] = [];
									for (let r: ReactTableRow | undefined = row.original; r; r = r.parent) path.unshift(r.id);
									lazyLoadRef.current.onLoadSubtree?.(lazyLoadRef.current.selectedPanel, path);
								}
								toggleProps.onClick?.(e);
							}}
						>
							{row.isExpanded ? '-' : '+'}
						</span>
					);
				},
				width: 60,
				Footer: 'Total'
			}
		]);
		if (props.goalsProps?.isGoals) {
			const columnOptionsGoals: DropdownData[] = [
				{ value: 'hide_lines_with_data', label: filterGoals ? translateToString('reports.column_options.show_all_lines') : translateToString('reports.column_options.hide_lines_with_data') },
			];

			const reportHeadersLength = report.headers.length;
			if (reportHeadersLength === 0) return cols;
			const lastHeaderTitle = report.headers[reportHeadersLength - 1]?.cell?.value?.val;
			if (lastHeaderTitle && 'date' in lastHeaderTitle) {
				const allDates = report.headers.reduce((acc: { date: moment.Moment, index: number }[], h, i) => {
					if (h.cell.value.val && 'date' in h.cell.value.val) {
						const date = Object.values(h.cell.value.val.date.val)[0];
						if (date) {
							acc.push({ date: moment(date), index: i });
						}
					}
					return acc;
				}, []);
				const granularity = Object.keys(lastHeaderTitle.date.val)[0];
				const typedGranularity = stringToGranularity(granularity);
				const reportHeadersNoDate: Header[] = [];
				const reportHeadersDate: string[] = [];
				report.headers.forEach(h => {
					if (h.cell.value.val) {
						if (!('date' in h.cell.value.val))
							reportHeadersNoDate.push(h);
						else {
							const date = getStartOfGranularity(typedGranularity, moment(Object.values(h.cell.value.val.date.val)[0]));
							reportHeadersDate.push(date.format('YYYY-MM-DD'));
						}
					}
				});
				setReportGranularity(granularity);
				if (typedGranularity) {
					let date = (pickedDates && pickedDates[0]) ? getStartOfGranularity(typedGranularity, moment(pickedDates[0])) : moment().startOf('year');
					const endDate = (pickedDates && pickedDates[1]) ? moment(pickedDates[1]) : moment().endOf('year');
					const isAllColsInsertedAfter = allDates[allDates.length - 1].date.isBefore(date) || allDates[allDates.length - 1].date.isSame(date);
					let i = 0;
					while (date.isBefore(endDate) || date.isSame(endDate)) {
						const formattedDate = date.format('YYYY-MM-DD');
						const formattedDateN1 = getNextGranularity(typedGranularity, moment(formattedDate), -1).format('YYYY-MM-DD');
						const closestDateIndex = allDates.reduce((closestIndex, currentDate, currentIndex) => {
							const currentDiff = Math.abs(currentDate.date.diff(date));
							const closestDiff = Math.abs(allDates[closestIndex].date.diff(date));
							return currentDiff < closestDiff ? currentIndex : closestIndex;
						}, 0);
						let averageValueForDate = getAverageValueForDate(formattedDate, reportHeadersNoDate.length - 1);
						let insertIndex = allDates[closestDateIndex].index;
						if (allDates[closestDateIndex].date.isAfter(date))
							insertIndex++;
						if (isAllColsInsertedAfter)
							insertIndex++;
						const footerIndex = reportHeadersDate.findIndex(d => d === formattedDate);
						const footerIndexN1 = reportHeadersDate.findIndex(d => d === formattedDateN1);
						let isAverage = true;
						let footerCellValue;
						let footerCellValueN1: number | null = null;
						if (footerIndex != -1) {
							const row: ReactTableRow = { id: -1, cells: [], primaryCell: [], subRows: datas };
							isAverage = isColAverage(row, footerIndex);
							footerCellValue = isAverage ? getAverageCellValueOfChildrenWithZeros(row, formattedDate, footerIndex) : getSumValueOfChildrenWithZeros(row, formattedDate, footerIndex);
							if (weightByRef && isAverage)
								averageValueForDate = getChildrenGoalSum(row, formattedDate, footerIndex);
						}
						if (footerIndexN1 != -1) {
							const row: ReactTableRow = { id: -1, cells: [], primaryCell: [], subRows: datas };
							isAverage = isColAverage(row, footerIndexN1);
							footerCellValueN1 = isAverage ? getAverageCellValueOfChildrenWithZeros(row, formattedDateN1, footerIndexN1, true) : getSumValueOfChildrenWithZeros(row, formattedDateN1, footerIndexN1);
						}
						let averageValueForDateRatio;
						if (averageValueForDate)
							averageValueForDateRatio = isAverage ? averageValueForDate.num / averageValueForDate.denom : averageValueForDate.num;
						const goalColumn = {
							id: `column[${reportHeadersLength + i * 4 + 1}]`,
							noHeaderEllipsis: true,
							Header: <FlexDiv width='95%'>
								<CellToNode cell={{ value: { val: { text: '🎯' }, tag: 1 }, link: null, tooltip: null }} fontSize='18px' />
								<Dropdown
									stopPropagation
									searchable={false}
									name='ColumnOptions'
									dropdownStyle={{ optionWidth: '230px', optionLeft: '-205px', height: 'auto' }}
									datalist={columnOptionsGoals}
									JSXButton={() => <ToolbarImageContainer hasPointer rotate='90deg' marginLeft='-3px'><Ellipsis color={GreySidely3} strokeWidth={2.5} /></ToolbarImageContainer>}
									onChange={(value: DropdownData) => {
										switch (value.value) {
											case 'hide_lines_with_data': {
												setFilterGoals((prev) => {
													return formattedDate === prev ? undefined : formattedDate;
												});
												break;
											}
										}
									}}
								/>
							</FlexDiv>,
							Footer: averageValueForDate !== undefined ?
								(
									isAverage ? <CellToNode cell={{ value: { val: { average: { numerator: averageValueForDate.num, denominator: averageValueForDate.denom } }, tag: 17 }, link: null, tooltip: null }} />
										:
										<CellToNode cell={{ value: { val: { float: averageValueForDate.num }, tag: 4 }, link: null, tooltip: null }} />
								)
								:
								<RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />,
							accessor: (row: ReactTableRow) => {
								const path = getPathFromRowWithDate(row, formattedDate);
								const pathLength = Object.keys(path).length - 1;
								let goalValue = goalPathsHashMap.get(goalPathHash(path));
								if (!goalValue && pathLength !== reportHeadersNoDate.length) {
									const averageGoalValue = getChildrenGoalValue(row, formattedDate, footerIndex, isAverage);
									if (averageGoalValue !== undefined) {
										goalValue = { value: averageGoalValue.toString(), index: pathLength, dashboard: false, evolution: undefined };
										goalPathsHashMap.set(goalPathHash(path), goalValue);
									}
								}
								return <GoalInputCell value={goalValue?.value ?? ''}
									onBlur={(value) => {
										const trimmedValue = value.trim();
										const newGoals = [...goals];
										const goalToUpdate = updateOneGoal(formattedDate, trimmedValue, path, goalValue, newGoals);
										const newGoalsFiltered = newGoals.filter(g => g.goals.length > 0 && Object.keys(g.path).length === reportHeadersNoDate.length);
										if (goalToUpdate) {
											if (goalToUpdate.deleted) {
												deleteGoals([{ ...goalToUpdate, date: goalToUpdate.goals[0].date }]);
											}
											else {
												postGoals([goalToUpdate]);
											}
										}
										setGoals(newGoalsFiltered);
									}}
									onSetAllColumn={(value) => {
										const trimmedValue = value.trim();
										const newGoals = [...goals];
										const goalsToDelete: GoalDelete[] = [];
										const goalsToUpdate: Goal[] = [];
										if (!row.parent?.subRows || row.parent.subRows.length === 0) {
											const goalToUpdate = updateOneGoal(formattedDate, trimmedValue, path, goalValue, newGoals);
											if (goalToUpdate) {
												if (goalToUpdate.deleted) {
													goalsToDelete.push({ ...goalToUpdate, date: goalToUpdate.goals[0].date });
												}
												else {
													goalsToUpdate.push(goalToUpdate);
												}
											}
										}
										else {
											setAllColumnGoals([trimmedValue], row, formattedDate, reportHeadersNoDate, newGoals, goalsToUpdate, goalsToDelete);
										}
										const newGoalsFiltered = newGoals.filter(g => g.goals.length > 0 && Object.keys(g.path).length === reportHeadersNoDate.length);
										if (goalsToUpdate.length > 0)
											postGoals(goalsToUpdate);
										if (goalsToDelete.length > 0)
											deleteGoals(goalsToDelete);
										setGoals(newGoalsFiltered);
									}}
									onSetAllColumnCustom={(value) => {
										const newGoals = [...goals];
										const goalsToDelete: GoalDelete[] = [];
										const goalsToUpdate: Goal[] = [];
										const valuesToSet: string[] = [];
										const lastColDate = getNextGranularity(typedGranularity, moment(formattedDate), -1).format('YYYY-MM-DD');
										if (row.parent) {
											const floatValue = parseFloat(value);
											row.parent.subRows.forEach((sibling) => {
												let valueToPush = value;
												const cellIndex = reportHeadersDate.findIndex(d => d === lastColDate);
												if (cellIndex !== -1) {
													const cell = sibling.cells[cellIndex].value;
													const cellValue = getCellValue(cell, props.goalsProps?.emptyGoalInterpretation ?? '0');
													if (cellValue !== null) {
														if (cellValue > floatValue)
															valueToPush = parseFloat(cellValue.toFixed(2)).toString();
													}
												}
												valuesToSet.push(valueToPush);
											});
										}
										if (valuesToSet.length > 1)
											setAllColumnGoals(valuesToSet, row, formattedDate, reportHeadersNoDate, newGoals, goalsToUpdate, goalsToDelete);
										else
											setAllColumnGoals([value], row, formattedDate, reportHeadersNoDate, newGoals, goalsToUpdate, goalsToDelete);
										const newGoalsFiltered = newGoals.filter(g => g.goals.length > 0 && Object.keys(g.path).length === reportHeadersNoDate.length);
										if (goalsToUpdate.length > 0)
											postGoals(goalsToUpdate);
										if (goalsToDelete.length > 0)
											deleteGoals(goalsToDelete);
										setGoals(newGoalsFiltered);
									}}
									isInputDisabled={!(meOwner || isAdmin) || pathLength !== reportHeadersNoDate.length}
								/>;
							},
						};
						const goalsEvolution = {
							id: `column[${reportHeadersLength + i * 4 + 2}]`,
							Header: <CellToNode cell={{ value: { val: { text: '%' }, tag: 1 }, link: null, tooltip: null }} />,
							Footer: averageValueForDate && footerCellValue !== undefined && footerCellValue !== null ?
								(
									props.goalsProps?.evolutionCalculus === GoalEvolutionCalculus.Nmin1 && footerCellValueN1 ?
										<CellToNode goal={true} cell={{ value: { val: { percentage: { numer: footerCellValue - footerCellValueN1 < 0 ? 0 : footerCellValue - footerCellValueN1 > 1 ? 1 : footerCellValue - footerCellValueN1, denom: footerCellValue - footerCellValueN1 > 1 ? 1 : (averageValueForDateRatio - footerCellValueN1) !== 0 ? (averageValueForDateRatio - footerCellValueN1) : 1 } }, tag: 0 }, link: null, tooltip: null }} />
										:
										<CellToNode goal={true} cell={{ value: { val: { percentage: { numer: footerCellValue, denom: averageValueForDateRatio } }, tag: 0 }, link: null, tooltip: null }} />
								)
								:
								(
									averageValueForDate && props.goalsProps.emptyGoalInterpretation !== 'null' ?
										<CellToNode goal={true} cell={{ value: { val: { percentage: { numer: 0, denom: 1 } }, tag: 0 }, link: null, tooltip: null }} />
										:
										<RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />
								),
							accessor: (row: ReactTableRow) => {
								const path = getPathFromRowWithDate(row, formattedDate);
								const pathLength = Object.keys(path).length - 1;
								const cellIndex = reportHeadersDate.findIndex(d => d === formattedDate);
								const cellIndexN1 = reportHeadersDate.findIndex(d => d === formattedDateN1);
								const goalFromHash = goalPathsHashMap.get(goalPathHash(path));
								let goalValue = goalFromHash?.value;
								if (!goalValue && pathLength !== reportHeadersNoDate.length)
									goalValue = getChildrenGoalValue(row, formattedDate, cellIndex, isAverage)?.toString();
								if (goalValue) {
									const goalValueNumber = parseFloat(goalValue);
									if (!goalValueNumber || isNaN(goalValueNumber)) {
										const evolutionCell: ComplexCell = { value: { tag: 15, val: null }, values: [], link: null, tooltip: null };
										setEvolutionHashMap(evolutionCell, path, goalFromHash);
										return <RenderTableCell primaryLength={primaryLength} data={evolutionCell} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />;
									}
									if (cellIndex !== -1) {
										let cellValue: number | null = null;
										let cellValueN1: number | null = null;
										if (pathLength === reportHeadersNoDate.length || props.goalsProps?.emptyGoalInterpretation === 'null') {
											const cell = row.cells[cellIndex].value;
											cellValue = getCellValue(cell, props.goalsProps?.emptyGoalInterpretation ?? '0');
											if (props.goalsProps?.evolutionCalculus === GoalEvolutionCalculus.Nmin1 && cellIndexN1 !== -1) {
												cellValueN1 = getCellValue(row.cells[cellIndexN1].value, props.goalsProps?.emptyGoalInterpretation ?? '0');
											}
										}
										else {
											cellValue = isAverage ? getAverageCellValueOfChildrenWithZeros(row, formattedDate, cellIndex) : getSumValueOfChildrenWithZeros(row, formattedDate, cellIndex);
											if (props.goalsProps?.evolutionCalculus === GoalEvolutionCalculus.Nmin1 && cellIndexN1 !== -1) {
												cellValueN1 = isAverage ? getAverageCellValueOfChildrenWithZeros(row, formattedDate, cellIndexN1) : getSumValueOfChildrenWithZeros(row, formattedDate, cellIndexN1);
											}
										}
										if (cellValue !== null) {
											let denom = 1;
											let numer = cellValue;
											if (cellValueN1) {
												numer = cellValue - cellValueN1;
												if (numer < 0)
													numer = 0;
												if ((goalValueNumber - parseFloat(cellValueN1.toFixed(2))) !== 0) {
													const resDenom = goalValueNumber - cellValueN1;
													if (resDenom > 0)
														denom = resDenom;
												}
											}
											else {
												numer = cellValue;
												denom = goalValueNumber;
											}
											if (props.goalsProps?.evolutionCalculus === GoalEvolutionCalculus.Nmin1 && (numer / denom > 1 || cellValueN1 && (cellValue === cellValueN1 && (cellValueN1 === goalValueNumber || cellValueN1 > goalValueNumber)))) {
												numer = 1;
												denom = 1;
											}
											const evolutionCell: Cell = { value: { val: { percentage: { numer: numer, denom: denom } }, tag: 0 }, link: null, tooltip: null };
											setEvolutionHashMap(evolutionCell, path, goalFromHash);
											return <CellToNode goal={true} cell={evolutionCell} />;
										}
										const evolutionCell: ComplexCell = { value: { tag: 15, val: null }, values: [], link: null, tooltip: null };
										setEvolutionHashMap(evolutionCell, path, goalFromHash);
										return <RenderTableCell primaryLength={primaryLength} data={evolutionCell} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />;
									}
									else {
										if (props.goalsProps?.emptyGoalInterpretation === 'null') {
											const evolutionCell: ComplexCell = { value: { tag: 15, val: null }, values: [], link: null, tooltip: null };
											setEvolutionHashMap(evolutionCell, path, goalFromHash);
											return <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />;
										}
										const evolutionCell: Cell = { value: { val: { percentage: { numer: 0, denom: 1 } }, tag: 0 }, link: null, tooltip: null };
										setEvolutionHashMap(evolutionCell, path, goalFromHash);
										return <CellToNode goal={true} cell={evolutionCell} />;
									}
								}
								const evolutionCell: ComplexCell = { value: { tag: 15, val: null }, values: [], link: null, tooltip: null };
								setEvolutionHashMap(evolutionCell, path, goalFromHash);
								return <RenderTableCell primaryLength={primaryLength} data={evolutionCell} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />;
							},
						};
						const goalsDashboard = {
							id: `column[${reportHeadersLength + i * 4 + 3}]`,
							Header: <FlexDiv justify='center'><DefaultImage width='18px' height='24px' src={DashboardImg} /></FlexDiv>,
							accessor: (row: ReactTableRow) => {
								const path = getPathFromRowWithDate(row, formattedDate);
								const goalValue = goalPathsHashMap.get(goalPathHash(path));
								const hasChildren = row.subRows && row.subRows.length > 0;
								const isCheckboxDisabled = hasChildren ? areNoChildrenSet(row, formattedDate) : !goalValue;
								const checked = hasChildren ? areAllChildrenSetDashboard(row, formattedDate) : goalValue?.dashboard ?? false;
								return <DashboardCheckbox
									checked={checked}
									isCheckboxDisabled={isCheckboxDisabled}
									onChange={() => {
										const goalsToUpdate: Goal[] = [];
										const newGoals = [...goals];
										updateDashboardRecursively(row, !checked, formattedDate, newGoals, goalsToUpdate);
										setGoals(newGoals);
										if (goalsToUpdate.length > 0)
											postGoals(goalsToUpdate);
									}}
									onCheckAll={() => {
										const goalsToUpdate: Goal[] = [];
										const newGoals = [...goals];
										updateAllDashboard(row, checked, formattedDate, newGoals, goalsToUpdate);
										setGoals(newGoals);
										if (goalsToUpdate.length > 0)
											postGoals(goalsToUpdate);
									}}
								/>;
							},
							...{ maxWidth: 60, minWidth: 60, width: 60 }
						};
						const goalsRest = {
							id: `delta[${reportHeadersLength + i * 4}]`,
							Header: <CellToNode cell={{ value: { val: { key: 'objectives.remaining' }, tag: 2 }, link: null, tooltip: null }} />,
							Footer: averageValueForDate && footerCellValue !== undefined && footerCellValue !== null ?
								<CellToNode goal={true} cell={{ value: { val: { float: Math.max(0, averageValueForDateRatio - footerCellValue) }, tag: 4 }, link: null, tooltip: null }} />
								:
								(
									averageValueForDate && props.goalsProps.emptyGoalInterpretation !== 'null' ?
										<CellToNode goal={true} cell={{ value: { val: { float: averageValueForDateRatio }, tag: 4 }, link: null, tooltip: null }} />
										:
										<RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />
								),
							accessor: (row: ReactTableRow) => {
								const path = getPathFromRowWithDate(row, formattedDate);
								const pathLength = Object.keys(path).length - 1;
								const cellIndex = reportHeadersDate.findIndex(d => d === formattedDate);
								const goalFromHash = goalPathsHashMap.get(goalPathHash(path));
								let goalValue = goalFromHash?.value;
								if (!goalValue && pathLength !== reportHeadersNoDate.length)
									goalValue = getChildrenGoalValue(row, formattedDate, cellIndex, isAverage)?.toString();
								if (goalValue) {
									const goalValueNumber = parseFloat(goalValue);
									if (isNaN(goalValueNumber)) {
										return <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />;
									}
									if (cellIndex !== -1) {
										const cell = row.cells[cellIndex].value;
										if (cell.tag === 0) {
											const { numer, denom } = cell.val.percentage;
											const goalNumer = Math.round(goalValueNumber / 100 * denom);
											return <CellToNode goal={true} cell={{ value: { val: { int: Math.max(0, goalNumer - numer) }, tag: 5 }, link: null, tooltip: null }} />;
										}
										const cellValue = getCellValue(cell, props.goalsProps?.emptyGoalInterpretation ?? '0');
										if (cellValue !== null) {
											const rest = Math.max(0, goalValueNumber - cellValue);
											return <CellToNode goal={true} cell={{ value: { val: { float: rest }, tag: 4 }, link: null, tooltip: null }} />;
										}
									}
								}
								return <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />;
							},
						};
						switch (granularity) {
							case 'week':
								if (!allDates.find(d => d.date.isSame(date, 'week'))) {
									const newCol = {
										id: `column[${reportHeadersLength + i * 4}]`,
										Header: <CellToNode cell={{ value: { val: { date: { val: { [granularity]: formattedDate }, tag: 1 as const } }, tag: 8 }, link: null, tooltip: null }} />,
										accessor: () => <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />,
										...{ maxWidth: 134, minWidth: 134, width: 134 }
									};
									cols.splice(insertIndex + i * (4 + (showDelta ? 1 : 0)), 0, newCol);
								}
								date.add(1, 'week');
								date = date.startOf('week');
								break;
							case 'month':
								if (!allDates.find(d => d.date.isSame(date, 'month'))) {
									const newCol = {
										id: `column[${reportHeadersLength + i * 4}]`,
										Header: <CellToNode cell={{ value: { val: { date: { val: { [granularity]: formattedDate }, tag: 2 as const } }, tag: 8 }, link: null, tooltip: null }} />,
										accessor: () => <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />,
										...{ maxWidth: 134, minWidth: 134, width: 134 }
									};
									cols.splice(insertIndex + i * (4 + (showDelta ? 1 : 0)), 0, newCol);
								}
								date.add(1, 'month');
								date = date.startOf('month');
								break;
							case 'quarter':
								if (!allDates.find(d => d.date.isSame(date, 'quarter'))) {
									const newCol = {
										id: `column[${reportHeadersLength + i * 4}]`,
										Header: <CellToNode cell={{ value: { val: { date: { val: { [granularity]: formattedDate }, tag: 3 as const } }, tag: 8 }, link: null, tooltip: null }} />,
										accessor: () => <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />,
										...{ maxWidth: 134, minWidth: 134, width: 134 }
									};
									cols.splice(insertIndex + i * (4 + (showDelta ? 1 : 0)), 0, newCol);
								}
								date.add(3, 'month');
								date = date.startOf('quarter');
								break;
							case 'fourMonths':
								if (!allDates.find(d => d.date.isSame(date, 'month') && d.date.month() % 4 === date.month() % 4)) {
									const newCol = {
										id: `column[${reportHeadersLength + i * 4}]`,
										Header: <CellToNode cell={{ value: { val: { date: { val: { [granularity]: formattedDate }, tag: 4 as const } }, tag: 8 }, link: null, tooltip: null }} />,
										accessor: () => <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />,
										...{ maxWidth: 134, minWidth: 134, width: 134 }
									};
									cols.splice(insertIndex + i * (4 + (showDelta ? 1 : 0)), 0, newCol);
								}
								date.add(4, 'month');
								date = date.startOf('month').subtract(date.month() % 4, 'months');
								break;
							case 'year':
								if (!allDates.find(d => d.date.isSame(date, 'year'))) {
									const newCol = {
										id: `column[${reportHeadersLength + i * 4}]`,
										Header: <CellToNode cell={{ value: { val: { date: { val: { [granularity]: formattedDate }, tag: 5 as const } }, tag: 8 }, link: null, tooltip: null }} />,
										accessor: () => <RenderTableCell primaryLength={primaryLength} data={{ value: { tag: 15, val: null }, values: [], link: null, tooltip: null }} header={report.headers[reportHeadersLength + i]} toolTipTemplate={null} index={reportHeadersLength + i} />,
										...{ maxWidth: 134, minWidth: 134, width: 134 }
									};
									cols.splice(insertIndex + i * (4 + (showDelta ? 1 : 0)), 0, newCol);
								}
								date.add(1, 'year');
								date = date.startOf('year');
								break;
							default:
								date.add(1, 'month');
								break;
						}
						cols.splice(allDates[0].index + i * (4 + (showDelta ? 1 : 0)) + 2, 0, goalColumn);
						cols.splice(allDates[0].index + i * (4 + (showDelta ? 1 : 0)) + 3, 0, goalsEvolution);
						if (showDelta) cols.splice(allDates[0].index + i * (4 + (showDelta ? 1 : 0)) + 4, 0, goalsRest);
						cols.splice(allDates[0].index + i * (4 + (showDelta ? 1 : 0)) + (showDelta ? 5 : 4), 0, goalsDashboard);
						i++;
					}
				}
			}
		}
		if (props.goalsProps?.setGoalHashMap && goalPathsHashMap) {
			props.goalsProps.setGoalHashMap(goalPathsHashMap);
		}
		return cols;
	}, [report, selectedPanel, selectedCheckbox, vizualisationFilterResult.formatted, additionalColumns, goalPathsHashMap, pickedDates, filterGoals, props.reprasentation, props.columnColorsFrom]);
	if (!report) return <NoData message={translateToString('no data')} isError={true} height='calc(100% - 10px)' />;

	return <FlexDiv flow='column' align='stretch' height='100%' width='100%'>
		{r.panels.length > 1 && <PanelSelector
			panels={r.panels.map(p => ({ label: <ValueToNode value={p.value} /> }))}
			onClick={(_, i) => {
				if (selectedPanel === i) return;
				selectedView === 'table' && setLoadingState(LoadingStateEnum.LOADING);
				setTimeout(() => { setSelectedPanel(i); props.goalsProps?.setSelectedPanel(i); }, 10);
			}}
			activeIndex={selectedPanel}
		/>}
		{selectedView === 'chart' && report.mainAxisRepresentation === AxisRepresentation.COMPANIES && <PanelSelector
			panels={report.headers.filter(h => !h.primary && h.visualisable).map(p => ({ label: <ValueToNode value={p.cell.value} /> }))}
			onClick={(_, i) => {
				if (selectedHeader === i) return;
				setTimeout(() => setSelectedHeader(i), 10);
			}}
			activeIndex={selectedHeader ?? 0}
		/>}
		<div style={{ backgroundColor: SurfaceColor, flexGrow: 1 }}>
			{selectedView === 'chart' && chart && <ChartReportingV2
				onCompanyClick={onCompanyClick}
				onFormInstanceClick={onFormInstanceClick}
				setToolBarState={() => null}
				chart={chart}
			/>}
			{selectedView === 'table' && <InfiniteTableStyle toolTipNotHidden>
				<InfiniteTable
					onLoad={onTableLoad}
					height={height}
					columns={columns}
					data={datas}
					autoResetExpanded={false}
					// panel-scoped: ids restart in every panel and lazily loaded
					// children only exist in the panel they were fetched for, so
					// expansion state must not carry across a panel switch
					getRowId={row => `${selectedPanel}:${row.id}`}
					initialState={columns.length ? { sortBy: [{ id: 'column[0]', desc: false }] } : undefined}
					header_thing={<Visibility defaultValue={hidden} onChange={(b) => { setHidden(b); }} />}
					setSelectedCheckbox={id => {
						if (selectedCheckbox.filter(b => b).length >= MAX_SELECTED && !selectedCheckbox[id]) return false;
						selectedCheckbox[id] = !(selectedCheckbox[id] ?? false);
						setSelectedCheckbox([...selectedCheckbox]);
						return selectedCheckbox[id];
					}}
					selectedCheckbox={selectedCheckbox}
					pinnedColumns={pinnedUntil}
					onPinColumns={pinColumnsUntil}
				/>
				<InfiniteTableToolTip />
			</InfiniteTableStyle>}
		</div>
		{props.filterParams !== undefined &&
			<AdvancedFilters
				isOpen={isFilterOpen}
				setOpen={setFilterOpen}
				isOpenSummary={isOpenSummary}
				filterList={props.filterParams}
				filterValues={filterResult.values}
				onChange={setFilterResult}
				goalsProps={props.goalsProps}
			/>
		}
		{props.visualizationFitlerParams !== undefined &&
			<AdvancedFilters
				title={translateToString('settings')}
				isOpen={isVizualisationFilterOpen}
				setOpen={setVizualisationFilterOpen}
				isOpenSummary={isOpenSummary}
				filterList={props.visualizationFitlerParams}
				filterValues={vizualisationFilterResult.values}
				onChange={setVizualisationFilterResult}
				extractFilters
				goalsProps={props.goalsProps}
			/>
		}
		<ComponentLoader loadingState={loadingState} allScreen noBackground />
	</FlexDiv>;
}

function valueToNumber(value: Value): number | null {
	if (typeof value === 'string' || !value || !value.val) return null;
	if ('int' in value.val) return value.val.int;
	if ('float' in value.val) return value.val.float;
	if ('percentage' in value.val) return fractionToPercentage(value.val.percentage);
	if ('average' in value.val) return value.val.average.numerator / value.val.average.denominator;
	return null;
}

type Fraction = {
	readonly numer: number,
	readonly denom: number,
} | [number, number] | number;

function fractionToDecimal(f: Fraction): number {
	if (Array.isArray(f)) return f[0] / f[1];
	if (typeof f === 'number') return f;
	return f.numer / f.denom;
}

function fractionToPercentage(f: Fraction): number {
	return fractionToDecimal(f) * 100;
}

// This function is revelant only if data is homogeneous
function getSecondaryAxisOption(cells: [readonly (Cell | Point)[], Cell, number][], headers: [string, number][]): SecondaryAxis | undefined {
	for (const cell of cells) {
		const headerCells = cell[0].filter((_, i) => headers.find(h => h[1] == i));
		for (const cell of headerCells) {
			if (typeof cell.value === 'string' || !cell.value) {
				continue;
			}
			if ('percentage' in cell.value) {
				return { min: 0, max: 100, ticksType: 'percentage', scale: null, title: '' };
			}
		}
	}
}

function reportToChart(r: Report, report: Panel, selectedPanel: number, selectedCheckbox: boolean[], colors: { rowColors: ColorAndT<number>[]; panelColors: ColorAndT<Panel>[]; }, selectedHeader: number, users: Owner[], products: Product[], companies: Record<number, string>, campaigns: Record<number, Campaign>): Chart {
	const primaryIndex = report.headers.reduce((acc, h, i) => h.primary ? i : acc, -1);
	const barChartOption = report.headers.find(h => !h.primary && h.visualisable)?.barChart[selectedPanel];
	const headers: [string, number][] = report.headers.reduce((acc, h, i) => {
		if (h.primary || !h.visualisable) return acc;
		const headerText = valueToString(h.cell.value);
		return headerText ? [...acc, [headerText, i - primaryIndex - 1]] : acc;
	}, []);
	const flattenCells = (acc: [readonly (Cell | ComplexCell)[], Cell, number][], row: Row): [readonly (Cell | ComplexCell)[], Cell, number][] => {
		acc = [...acc, ...row.rows.reduce(flattenCells, [])];
		if (!selectedCheckbox[row.id]) return acc;
		return [...acc, [row.cells, (row.primaryCell.find(p => p.value.tag != EmptyCell.tag) ?? row.cells?.find(c => c.value.tag != EmptyCell.tag))!, row.id]];
	};
	const flattenRow = (acc: Row[], row: Row): Row[] => {
		acc = [...acc, ...row.rows.reduce(flattenRow, [])];
		if (!selectedCheckbox[row.id]) return acc;
		return [...acc, row];
	};
	const rows = report.rows.reduce(flattenRow, []);
	const cells: [readonly (Cell | ComplexCell)[], Cell, number][] = report.rows.reduce(flattenCells, []);
	const secondaryAxisOptions = getSecondaryAxisOption(cells, headers);
	const stacked = report.stacked || selectedCheckbox.filter(b => b).length === 1 && cells[0] && cells[0][0] && cells[0][0][0] && 'values' in cells[0][0][0] && cells[0][0][0].values.length > 0;
	return {
		stacked,
		title: valueToString(r.title) ?? '',
		curves: report.mainAxisRepresentation === AxisRepresentation.COMPANIES
			? genCompaniesCurves(r.panels[selectedPanel], rows, selectedHeader)
			: cells.reduce((acc: Curve[], value, _i): Curve[] => {
				if (selectedCheckbox.filter(b => b).length === 1 && value[0] && value[0][0] && 'values' in value[0][0] && value[0][0].values.length > 0) {
					acc.push({
						curveValues: value[0].filter((_, i) => headers.find(h => h[1] == i)).map(cell => valueToNumber(cell.value)),
						link: primaryIndex < 0 ? null : value[1].link?.val ?? null,
						color: stringToColour('Total'),
						title: 'Total',
						representation: 'CURVE'
					});
					for (let subPanelIndex = 0; subPanelIndex < value[0][0].values.length; subPanelIndex++) {
						const curveValues = headers.map(([_, headerIndex]) => {
							const complexCell = value[0][headerIndex];
							if (!complexCell || !('values' in complexCell)) return null;
							return valueToNumber(complexCell.values[subPanelIndex]);
						});
						acc.push({
							curveValues,
							link: primaryIndex < 0 ? null : value[1].link?.val ?? null,
							color: barChartOption?.[subPanelIndex].color ?? '',
							title: barChartOption?.[subPanelIndex].name ? valueToString(barChartOption[subPanelIndex].name) ?? '' : '',
							representation: 'BAR_CHART'
						});
					}
				} else {
					const title = valueToString(value[1].value, users, products, companies, campaigns);
					const v = {
						curveValues: value[0].filter((_, i) => headers.find(h => h[1] == i)).map(cell => valueToNumber(cell.value)),
						link: primaryIndex < 0 ? null : value[1].link?.val ?? null,
						color: title ? stringToColour(title) : (colors.rowColors.find(c => c.value === value[2])?.color ?? ''),
						title: title ?? '',
						representation: r.panels[selectedPanel].representation
					};
					acc.push(v);
				}
				return [...acc];
			}, []),
		mainAxis: {
			// TODO
			title: '',
			labels: report.mainAxisRepresentation === AxisRepresentation.COMPANIES ? genCompaniesAxis(rows) : headers.map(h => h[0]),
			key: report.mainAxisKey,
			// TODO
			ticksType: 'string'
		},
		secondaryAxis: secondaryAxisOptions ?? {
			// TODO
			title: '',
			// TODO
			min: null,
			// TODO
			max: null,
			// TODO
			scale: null,
			// TODO
			ticksType: 'string'
		}
	};
}

function genCompaniesAxis(rows: Row[]): string[] {
	return rows.map(row => valueToString(row.primaryCell[0].value) ?? '');
}

function genCompaniesCurves(report: Panel, rows: Row[], selectedHeader: number): Curve[] {
	const index = report.headers.filter(h => !h.primary).map((h, i) => ({ keep: h.visualisable && !h.primary, i })).filter(h => h.keep)[selectedHeader]?.i;
	if (index === undefined) return [];
	const firstValue: Value = rows.reduce((acc: Value, r) => {
		if (acc.val !== null) return acc;
		return r.cells[index]?.value ?? EmptyCell;
	}, EmptyCell);

	if (firstValue.val && 'pieChart' in firstValue.val) {
		const res: { [key: string]: { values: (number | undefined)[], title: string } } = rows.reduce((acc, r) => {
			const val = r.cells[index];
			if (!val || !val.value.val || !('pieChart' in val.value.val)) return acc;
			const total = val.value.val.pieChart.reduce((acc, v) => acc + v.count, 0);
			val.value.val.pieChart.forEach((v, i) => {
				const color = stringToColour(v.name);
				if (!acc[color]) {
					let title = '';
					if (val.tooltip?.content.val && 'table' in val.tooltip.content.val) {
						title = valueToString(val.tooltip.content.val.table[i][0]) ?? '';
					}
					acc[color] = { title, values: [] };
				}
				acc[color].values[i] = v.count / total;
			});
			return acc;
		}, {});
		return Object.entries(res).map(([color, curveValues]) => ({
			curveValues: curveValues.values.map(v => v ?? 0),
			link: null,
			color,
			title: curveValues.title,
			representation: 'BAR_CHART'
		}));
	} else {
		const curveValues = rows.reduce((acc, r) => [...acc, valueToNumber(r.cells[index]?.value)], []);
		return [{
			curveValues,
			link: null,
			color: PrimaryColor,
			title: valueToString(report.headers.filter(h => !h.primary)[index].cell.value) ?? 'values',
			representation: 'BAR_CHART'
		}];
	}
}

const ToolTipContainer = styled.div`
	border: none !important;
	height: 0 !important;
	width: 0 !important;
`;

export function InfiniteTableToolTip(): JSX.Element {
	const context = React.useContext(ReportInterpretorContext);
	if (!context.hover) return <></>;
	const { cell, bounds, toolTipTemplate, primaryCell, pos } = context.hover;
	const t = { ...context.hover };
	let tooltipClass = 'tooltip2';
	let tooltipTextClass = 'tooltiptext2';
	if (pos == 'up') {
		tooltipClass = 'tooltipup';
		tooltipTextClass = 'tooltiptextup';
	}

	if (cell.value.val && 'percentage' in cell.value.val) {
		return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
			<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
				<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
					{primaryCell && <div className='font-weight-bold' style={{ marginBottom: '2px' }}>
						<CellToNode cell={primaryCell} primary />
					</div>}
					{cell.value.val.percentage.numer}/{cell.value.val.percentage.denom}
				</FlexDiv>
			</div>
		</ToolTipContainer>;
	}

	if (cell.value.val && 'evolution' in cell.value.val) {
		return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
			<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
				<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
					{primaryCell && <div className='font-weight-bold' style={{ marginBottom: '2px' }}>
						<CellToNode cell={primaryCell} primary />
					</div>}
					<ValueToNode value={cell.value.val.evolution.old} />
				</FlexDiv>
			</div>
		</ToolTipContainer>;
	}

	if (cell.value.val && 'average' in cell.value.val) {
		return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
			<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
				<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
					{primaryCell && <div className='font-weight-bold' style={{ marginBottom: '2px' }}>
						<CellToNode cell={primaryCell} primary />
					</div>}
					{`${cell.value.val.average.numerator.toFixed(2)} / ${cell.value.val.average.denominator}`}
				</FlexDiv>
			</div>
		</ToolTipContainer>;
	}

	if (cell.value.val && 'pieChart' in cell.value.val) {
		const total = cell.value.val.pieChart.reduce((acc, v) => acc + v.count, 0);
		const array = [...cell.value.val.pieChart];
		array.sort((a, b) => b.count - a.count);
		return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
			<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
				<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
					{primaryCell && <div className='font-weight-bold' style={{ marginBottom: '2px' }}>
						<CellToNode cell={primaryCell} primary />
					</div>}
					<table>
						{array.map(({ count, name }, i) => <tr key={`toolTip[${i}]`}>
							<td style={{ backgroundColor: stringToColour(name), width: '10px', height: '10px' }} />
							<td>{name}</td>
							<td>{count} ({((count / total) * 100).toFixed(0)}%)</td>
						</tr>)}
						<td></td><td></td><td>{total}</td>
					</table>
				</FlexDiv>
			</div>
		</ToolTipContainer>;
	}

	if (cell.value.val && 'list' in cell.value.val) {
		const total = cell.value.val.list.reduce((acc, v) => acc + v.count, 0);
		return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
			<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
				<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
					<table>
						{cell.value.val.list.map(({ value, count }, i) => <tr key={`toolTip[${i}]`}>
							<td>{value}</td>
							<td>{count}</td>
						</tr>)}
						<td></td><td>{total}</td>
					</table>
				</FlexDiv>
			</div>
		</ToolTipContainer>;
	}

	if (cell.tooltip) {
		let toolTipContent: React.ReactNode = <></>;

		if ('simpleValue' in cell.tooltip.content.val) {
			toolTipContent = <ValueToNode value={cell.tooltip.content.val.simpleValue} />;
		} else if ('table' in cell.tooltip.content.val) {
			toolTipContent = <ToolTipTable>
				{cell.tooltip.content.val.table.map((values, x) => <tr key={`toolTip[${x}]`}>
					{values.map((value, y) => <ToolTipTd key={`toolTip[${x}][${y}]`} noBorder={x === 0} alignment={styleValue(value).justify}>{<ValueToNode value={value} />}</ToolTipTd>)}
				</tr>)}
			</ToolTipTable>;
		}
		return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
			<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
				<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
					{toolTipContent}
				</FlexDiv>
			</div>
		</ToolTipContainer>;
	}
	if (toolTipTemplate) {
		const title = referenceToNode(toolTipTemplate.title, primaryCell, cell);
		return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
			<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
				<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
					{title && <div className='font-weight-bold' style={{ marginBottom: '2px' }}>
						{title}
					</div>}
					<FlexDiv align='center' width='100%'>
						<ToolTipTable>
							{toolTipTemplate.content.map((values, x) => <tr key={`toolTip[${x}]`}>
								{values.map((value, y) => {
									// a template cell is a value or a pointer to one; either way
									// the tooltip aligns it the way the table aligns it
									const shown = 'value' in value.val ? value.val.value : referenceToValue(value.val.reference, primaryCell, cell);
									const nodeValue: React.ReactNode = shown === undefined ? undefined : <ValueToNode value={shown} />;
									return <ToolTipTd key={`toolTip[${x}][${y}]`} noBorder={x === 0 && title === undefined} alignment={shown && styleValue(shown).justify}>{nodeValue}</ToolTipTd>;
								})}
							</tr>)}
						</ToolTipTable>
					</FlexDiv>
				</FlexDiv>
			</div>
		</ToolTipContainer>;
	}

	return <ToolTipContainer className='table' onMouseEnter={() => context.setHover(t)} onMouseLeave={() => context.setHover(undefined)}>
		<div className={tooltipClass} style={{ position: 'absolute', left: bounds.left, top: bounds.top }}>
			<FlexDiv justify='center' flow='column' gap='10px' className={tooltipTextClass}>
				{primaryCell && <div className='font-weight-bold' style={{ marginBottom: '2px' }}>
					<CellToNode cell={primaryCell} primary />
				</div>}
				<ValueToNode value={cell.value} />
			</FlexDiv>
		</div>
	</ToolTipContainer>;
}

function EvolutionDropdown(props: { headerIndex: number }) {
	const { evolutionState, setEvolutionState } = React.useContext(ReportInterpretorContext);
	const currentState = evolutionState[props.headerIndex] ?? '%';
	return <Dropdown
		dropdownStyle={{
			height: '20px',
			optionWidth: '60px'
		}}
		datalist={[{ label: '%', value: '%' }, { label: '+', value: '+' }]}
		name={`Operator[${props.headerIndex}]`}
		selectedValue={{ label: currentState, value: currentState }}
		JSXOption={({ options, onOptionClicked }) => {
			return <>{options.map((element, i) => <OptionBlock key={i} onClick={(e) => {
				e.stopPropagation();
				onOptionClicked(element, i);
			}}>{element.label}</OptionBlock>)}</>;
		}}
		JSXButton={({ value, setIsOpen }) => {
			return <EvolutionOperatorContainer onClick={(e) => {
				e.stopPropagation();
				setIsOpen(isOpen => !isOpen);
			}}>{value?.label}</EvolutionOperatorContainer>;
		}}
		onChange={value => setEvolutionState(state => {
			state[props.headerIndex] = value.value;
			return { ...state };
		})}
	/>;
}
